import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ThemeProvider, CssBaseline } from '@mui/material';
import {
  AppBar, Toolbar, Box, Container, Card, CardContent,
  Typography, Select, MenuItem, FormControl, InputLabel,
  Button, TextField, Chip, Link, Divider, CircularProgress,
  ListSubheader, Alert,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import theme from './theme';
import { loginRequest } from './authConfig';

const V0_API_KEY   = process.env.REACT_APP_V0_API_KEY;
const GITHUB_PAT   = process.env.REACT_APP_GITHUB_PAT;
const GITHUB_OWNER = process.env.REACT_APP_GITHUB_OWNER;
const GITHUB_REPO  = process.env.REACT_APP_GITHUB_REPO;
// v0 source repo: where v0's "Connect to GitHub" feature auto-pushes the
// rendered prototype source. Each chat lives on its own branch. Submission
// Tool resolves chatName → branch + SHA at submit time; Spec Writer Agent
// clones from that pinned SHA in Pass 1. See:
//   .specify/memory/permanent-factory-architecture.md  (Component 1)
const V0_SOURCE_REPO = process.env.REACT_APP_V0_SOURCE_REPO || 'Bonsai-LLC/mybonsai_version_1';
const ADO_ORG      = process.env.REACT_APP_ADO_ORG;
const ADO_PROJECT  = process.env.REACT_APP_ADO_PROJECT;
const ADO_PAT      = process.env.REACT_APP_ADO_PAT;

// ── PO LIST + PER-PO V0 KEYS ────────────────────────────────────────────────
const PO_LIST_RAW   = process.env.REACT_APP_PO_LIST || '';
const AVAILABLE_POS = PO_LIST_RAW.split(',').map((s) => s.trim()).filter(Boolean);

const PO_TO_KEY_SUFFIX = {
  'Liz Hayden':     'LIZ',
  'Joshua Remer':   'JOSH',
  'Khurram Saleem': 'KHURRAM',
};

const V0_KEYS_BY_SUFFIX = {
  LIZ:     process.env.REACT_APP_V0_API_KEY_LIZ     || '',
  JOSH:    process.env.REACT_APP_V0_API_KEY_JOSH    || '',
  KHURRAM: process.env.REACT_APP_V0_API_KEY_KHURRAM || '',
};

const PO_STORAGE_KEY        = 'mybonsai_submission_selectedPO';
const LAST_SUBMISSION_KEY   = 'mybonsai_submission_lastSubmission';

const getKeySuffix = (po) => {
  if (!po) return null;
  return PO_TO_KEY_SUFFIX[po] || po.toUpperCase();
};

const getV0KeyForPO = (po) => {
  if (!po) return V0_API_KEY;
  const suffix = getKeySuffix(po);
  return V0_KEYS_BY_SUFFIX[suffix] || V0_API_KEY;
};

// ── Match signed-in email → PO display name ────────────────────────────────
// Tokenises each PO name on whitespace and requires every token to appear in
// the email's local part (case-insensitive).
//   "Liz Hayden"     + "liz.hayden@bonsai.com"      → match
//   "Khurram Saleem" + "khurramsaleem@bonsai.com"   → match (concatenated)
//   "Liz Hayden"     + "lhayden@bonsai.com"         → no match (no "liz" token)
function matchPOFromEmail(email, availablePOs) {
  if (!email || !availablePOs?.length) return null;
  const local = email.split('@')[0].toLowerCase();
  for (const po of availablePOs) {
    const tokens = po.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) continue;
    if (tokens.every((t) => local.includes(t))) return po;
  }
  return null;
}

// ── ADO PROJECT SETUP ───────────────────────────────────────────────────────
// Target: 'mybonsai-ai' project — Agile process. Custom fields/states already
// configured (Phase 2 done). Tickets created in "Prototype Review" state.
// Title pattern: BONSAI-{System.Id}: <feature name>, set via 2-step POST+PATCH.

const githubHeaders = {
  Authorization: `token ${GITHUB_PAT}`,
  Accept: 'application/vnd.github.v3+json',
};

const adoHeaders = {
  Authorization: `Basic ${btoa(`:${ADO_PAT}`)}`,
  'Content-Type': 'application/json-patch+json',
};

const STATUS_COLOR = { idle: 'default', loading: 'warning', success: 'success', error: 'error' };

// ── Helpers ──────────────────────────────────────────────────────────────────

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const candidate = data.projects ?? data.chats ?? data.data ?? data.items ?? data.results ?? data.value ?? data.messages;
    if (Array.isArray(candidate)) return candidate;
    if (data.id || data.name) return [data];
  }
  return [];
};

const extractMessageText = (content) => {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === 'string') return part;
        if (part && typeof part === 'object') {
          if (part.type && part.type !== 'text') return '';
          return part.text || part.content || '';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n\n');
  }
  if (typeof content === 'object') {
    if (typeof content.text === 'string') return content.text;
    if (Array.isArray(content.parts)) return extractMessageText(content.parts);
  }
  return '';
};

const normalisePrompt = (text) =>
  text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const formatPrompts = (messages) => {
  const userMsgs = (messages || []).filter(
    (m) => m && m.role === 'user' && m.type !== 'open-in-v0'
  );
  const cleaned = userMsgs
    .map((m) => normalisePrompt(extractMessageText(m.content)))
    .filter(Boolean);
  return cleaned.map((p, i) => `[${i + 1}] ${p}`).join('\n\n');
};

// v0 source extraction lives elsewhere now: each v0 chat is connected to the
// `Bonsai-LLC/mybonsai_version_1` GitHub repo via v0's "Connect to GitHub"
// feature, which auto-pushes the rendered source to a branch per chat. The
// Submission Tool resolves chatId → branch + commit SHA at submission time
// and records that binding in three ADO custom fields. Pass 1 of the Spec
// Writer Agent then clones that exact SHA before reading source.
//
// v0's Platform API does NOT expose generated source code for standalone
// chats — all /files endpoints return 404, and latestVersion.files carries
// "GENERATING\n" stubs only. See:
//   .specify/memory/factory-build-status.md      (v0 API dead-end findings)
//   .specify/memory/permanent-factory-architecture.md  (Component 1 design)

/**
 * Make an attachment path safe for the GitHub Contents API. Normalises
 * separators, strips leading slashes/dot-segments, refuses path traversal.
 * Used by buildAttachmentPath when writing PO uploads under
 * `prototypes/{chatId}/{assets,attachments}/`.
 */
const sanitizeV0FilePath = (rawPath) => {
  if (!rawPath) return '';
  return String(rawPath)
    .replace(/\\/g, '/')                   // windows -> unix
    .replace(/^\.?\/+/, '')                  // strip leading "./" or "/"
    .split('/')
    .map((seg) => seg.replace(/[^\w.\-+]/g, '_'))
    .filter((seg) => seg && seg !== '.' && seg !== '..')
    .join('/');
};

/**
 * Build the storage filename for a v0 chat attachment.
 *
 * v0 reports the same `name` ("image.png") for every clipboard-pasted or
 * drag-dropped image the PO uploads. The unique identifier lives in the
 * blob URL, not the name. Writing all attachments under their literal
 * name would collide on every push and only the last upload would
 * survive in GitHub.
 *
 * Prefixing with the attachment's 1-based index, zero-padded, keeps all
 * uploads distinct while preserving the original name for debugging.
 *
 *   index 0, name "image.png"        → "01-image.png"
 *   index 2, name "image.png"        → "03-image.png"  (the logo)
 *   index 4, name "bp-demo (4).html" → "05-bp-demo__4_.html"
 */
const buildAttachmentPath = (a, i) => {
  const sanitized = sanitizeV0FilePath(a?.name || '');
  if (!sanitized) return '';
  const prefix = String(i + 1).padStart(2, '0');
  return `${prefix}-${sanitized}`;
};

// PHASE 1.2: v0 chat-attachment + assistant-decisions + screenshot capture.
//
// After hitting a real v0 chat we found that `latestVersion.files` returns
// stubs ("GENERATING\n") rather than the rendered React source. The actual
// design source the PO worked with is in three other places:
//
//   1. messages[].attachments[]
//      The PO's uploaded files: logo PNGs, reference screenshots, the
//      BlackPine HTML template. Stored as PUBLIC Vercel Blob URLs (no auth).
//
//   2. latestVersion.screenshotUrl
//      v0's own rendered screenshot of the final design. Needs Bearer auth.
//
//   3. messages[] (role=assistant) .content
//      The assistant's plain-text running commentary, where every design
//      decision is recorded with exact hex codes, fonts, file paths, etc.
//      This is what Pass 1 should ground on.
//
// All three get pushed to GitHub under prototypes/{chatId}/ so the Spec
// Writer Agent can read them as authoritative design inputs.

/**
 * Walk chatDetail.messages and flatten every user attachment into a list.
 * Vercel Blob URLs are public so no auth is needed to fetch them later.
 *
 * Returns: array of { url, name, contentType, size, messageIndex,
 *                     messageRole, isImage, isText }
 */
const extractV0Attachments = (chatDetail) => {
  if (!chatDetail) return [];
  const messages = Array.isArray(chatDetail.messages) ? chatDetail.messages : [];
  const out = [];
  messages.forEach((m, idx) => {
    const atts = Array.isArray(m?.attachments) ? m.attachments : [];
    atts.forEach((a) => {
      if (!a?.url || !a?.name) return;
      const contentType = String(a.contentType || '');
      const isImage = contentType.startsWith('image/');
      const isText  = contentType.startsWith('text/') ||
        /\.(html?|css|s[ac]ss|jsx?|tsx?|json|md|svg|txt|ya?ml)$/i.test(a.name);
      out.push({
        url: a.url,
        name: a.name,
        contentType,
        size: typeof a.size === 'number' ? a.size : 0,
        messageIndex: idx,
        messageRole: m.role || 'unknown',
        isImage,
        isText,
      });
    });
  });
  return out;
};

/**
 * Pull the assistant's plain-text content from each message. These are the
 * design-decision narratives: hex codes, font choices, file paths that v0
 * changed, etc. Strips empty bodies and the experimental_content AST.
 *
 * Returns: array of { index, content, createdAt }
 */
const extractAssistantChanges = (chatDetail) => {
  if (!chatDetail) return [];
  const messages = Array.isArray(chatDetail.messages) ? chatDetail.messages : [];
  let i = 0;
  return messages
    .filter((m) => m?.role === 'assistant' && typeof m.content === 'string' && m.content.trim())
    .map((m) => ({
      index: ++i,
      content: String(m.content),
      createdAt: m.createdAt || null,
    }));
};

/**
 * Pull latestVersion.screenshotUrl from the chat detail. The URL needs the
 * v0 API key as Bearer token to fetch the PNG body.
 *
 * Returns: { url, versionId } | null
 */
const extractV0Screenshot = (chatDetail) => {
  const url = chatDetail?.latestVersion?.screenshotUrl;
  if (!url) return null;
  return {
    url,
    versionId: chatDetail?.latestVersion?.id || '',
  };
};

/**
 * Build a list of screenshot URL candidates from the chat detail, ordered
 * most-likely-current first. Defensively walks multiple shapes because v0's
 * API doesn't document its version-list structure, and chats whose last
 * user turn was a code-export (e.g. "convert to HTML I can share") end up
 * with a blank latestVersion screenshot — but earlier UI-iteration versions
 * still have real renders.
 *
 * Sources tried, in order:
 *   1. chatDetail.latestVersion.screenshotUrl                  (current default)
 *   2. chatDetail.latestVersion.previousVersions[].screenshotUrl
 *   3. chatDetail.versions[].screenshotUrl
 *   4. chatDetail.history[].screenshotUrl
 *   5. chatDetail.messages[].screenshotUrl or .version.screenshotUrl
 *
 * Dedupes on URL + versionId so we don't fetch the same image twice.
 * Returns: array of { url, versionId, label, sourceField }
 */
const extractV0ScreenshotCandidates = (chatDetail) => {
  if (!chatDetail) return [];
  const candidates = [];
  const seenUrls = new Set();
  const seenVersionIds = new Set();

  const push = (url, versionId, label, sourceField) => {
    if (!url || seenUrls.has(url)) return;
    if (versionId && seenVersionIds.has(versionId)) return;
    seenUrls.add(url);
    if (versionId) seenVersionIds.add(versionId);
    candidates.push({ url, versionId: versionId || '', label, sourceField });
  };

  // 1. latestVersion (highest priority — the current/default behaviour)
  if (chatDetail.latestVersion?.screenshotUrl) {
    push(
      chatDetail.latestVersion.screenshotUrl,
      chatDetail.latestVersion.id || '',
      'latestVersion',
      'latestVersion.screenshotUrl'
    );
  }

  // 2. latestVersion.previousVersions[] (if v0 embeds a history). Walk in
  //    reverse so most-recent-previous is tried first.
  const prev = Array.isArray(chatDetail.latestVersion?.previousVersions)
    ? chatDetail.latestVersion.previousVersions
    : [];
  for (let i = prev.length - 1; i >= 0; i--) {
    const v = prev[i];
    if (v?.screenshotUrl) {
      push(v.screenshotUrl, v.id || '', `previous v${i + 1}`, 'latestVersion.previousVersions');
    }
  }

  // 3. chatDetail.versions[] — flat versions list (also walked reverse)
  const versions = Array.isArray(chatDetail.versions) ? chatDetail.versions : [];
  for (let i = versions.length - 1; i >= 0; i--) {
    const v = versions[i];
    if (v?.screenshotUrl) {
      push(v.screenshotUrl, v.id || '', `versions[${i}]`, 'versions[]');
    }
  }

  // 4. chatDetail.history[] — alternate naming some APIs use
  const history = Array.isArray(chatDetail.history) ? chatDetail.history : [];
  for (let i = history.length - 1; i >= 0; i--) {
    const h = history[i];
    if (h?.screenshotUrl) {
      push(h.screenshotUrl, h.versionId || h.id || '', `history[${i}]`, 'history[]');
    }
  }

  // 5. Walk messages for embedded version refs with screenshot URLs.
  //    v0's messages[] sometimes carries a versionId or nested version
  //    object on assistant responses that produced a UI delta.
  const messages = Array.isArray(chatDetail.messages) ? chatDetail.messages : [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.screenshotUrl && m?.versionId) {
      push(m.screenshotUrl, m.versionId, `message ${i}`, 'messages[].screenshotUrl');
    }
    if (m?.version?.screenshotUrl) {
      push(
        m.version.screenshotUrl,
        m.version.id || m.versionId || '',
        `message ${i} version`,
        'messages[].version.screenshotUrl'
      );
    }
  }

  return candidates;
};

/**
 * Try each screenshot candidate in order until one comes back above the
 * blank-filter threshold (20 KB). Calls the SWA backend proxy directly
 * (not via fetchAsBase64) so we get the size field back in the response.
 *
 * Logs each attempt to the activity log so the PO can see the tool tried
 * multiple versions before falling back.
 *
 * Returns: { base64, size, contentType, versionId, label } | null
 */
const findUsableV0Screenshot = async (candidates, v0Key, addLog) => {
  const MIN_BYTES = 20_000;          // anything smaller is a blank placeholder
  const MAX_ATTEMPTS = 6;             // don't hammer v0's API
  const toTry = candidates.slice(0, MAX_ATTEMPTS);

  for (const c of toTry) {
    try {
      addLog(`Trying screenshot from ${c.label} (${c.versionId || 'no id'})…`);
      const res = await axios.post('/api/v0-screenshot', { url: c.url, v0Key });
      const size = res.data?.size || 0;
      if (size < MIN_BYTES) {
        addLog(`  ✗ blank (${size.toLocaleString()} B); trying next candidate`);
        continue;
      }
      addLog(`  ✓ usable (${size.toLocaleString()} B) from ${c.label}`);
      return {
        base64: res.data.contentBase64,
        size,
        contentType: res.data.contentType,
        versionId: c.versionId,
        label: c.label,
      };
    } catch (err) {
      const reason = err.response?.data?.error || err.message;
      addLog(`  ✗ fetch failed: ${reason}; trying next candidate`);
    }
  }
  if (candidates.length > MAX_ATTEMPTS) {
    addLog(`  (${candidates.length - MAX_ATTEMPTS} more candidates skipped to avoid hammering v0)`);
  }
  return null;
};

/**
 * Fetch a URL and return its bytes base64-encoded, ready for the GitHub
 * Contents API. Used for binary attachments (images) and the v0 screenshot.
 *
 *   - Public Vercel Blob URLs: fetched directly from the browser.
 *   - Auth-protected v0 endpoints (screenshot): routed through the SWA
 *     backend at /api/v0-screenshot. The browser can't add the Bearer
 *     header without triggering a CORS preflight that v0's screenshot
 *     endpoint doesn't answer.
 */
const fetchAsBase64 = async (url, opts = {}) => {
  if (opts.v0Key) {
    const res = await axios.post('/api/v0-screenshot', { url, v0Key: opts.v0Key });
    if (!res.data || !res.data.contentBase64) {
      throw new Error(`proxy returned no content for ${url.slice(0, 80)}`);
    }
    return res.data.contentBase64;
  }
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} fetching ${url.slice(0, 80)}`);
  }
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

/**
 * Pick a more informative first line from an assistant response. Prefers
 * lines that mention hex codes, font names, or file paths, since those are
 * the design values the PO actually cares to scan. Falls back to the first
 * non-empty line otherwise. Bounded to 140 chars.
 */
const meaningfulFirstLine = (content) => {
  if (!content) return '';
  const lines = String(content).split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return '';
  const designRe = /#[0-9a-f]{3,8}\b|\.tsx?\b|\.css\b|\.scss\b|font|Figtree|Cormorant|Jost|color|background|palette|brand/i;
  const candidate = lines.find((l) => designRe.test(l));
  return (candidate || lines[0]).slice(0, 140);
};

/**
 * Extract distinct 6-digit hex codes from a string, normalised to uppercase
 * with a leading #. Returns up to maxCount unique values. Used to show
 * inline colour-swatch dots on each decision row for quick visual scanning.
 */
const extractHexCodes = (content, maxCount = 3) => {
  if (!content) return [];
  const matches = String(content).match(/#[0-9a-f]{6}\b/gi) || [];
  const seen = new Set();
  const out = [];
  for (const m of matches) {
    const norm = '#' + m.slice(1).toUpperCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(norm);
    if (out.length >= maxCount) break;
  }
  return out;
};

/**
 * Aggregate design-relevant facts across every assistant response. The PO
 * usually doesn't want to read 21 individual responses; they want to know
 * 'yes, these brand colours / fonts / files were captured'. This produces
 * that summary in one pass.
 *
 * Returns: { hex: [...], fonts: [...], files: [...], revisions: N }
 *   - hex:       distinct uppercase #RRGGBB codes mentioned anywhere
 *   - fonts:     distinct font names matched against a hint list
 *   - files:     distinct file paths with code-ish extensions
 *   - revisions: count of responses that start with apology/correction text
 */
const FONT_HINTS = [
  'Figtree', 'Cormorant Garamond', 'Cormorant', 'Jost',
  'Inter', 'Roboto', 'Playfair Display', 'Playfair',
  'Montserrat', 'Lato', 'Open Sans', 'Poppins',
  'Helvetica', 'Arial', 'Georgia', 'Times New Roman',
  'Source Sans', 'Source Serif', 'IBM Plex', 'Nunito',
];

const summarizeDecisions = (changes) => {
  if (!changes || changes.length === 0) {
    return { hex: [], fonts: [], files: [], revisions: 0 };
  }
  const fullText = changes.map((c) => c.content || '').join('\n');

  // Distinct hex codes (uppercase, leading #)
  const hexMatches = fullText.match(/#[0-9a-f]{6}\b/gi) || [];
  const hexSeen = new Set();
  const hex = [];
  for (const m of hexMatches) {
    const norm = '#' + m.slice(1).toUpperCase();
    if (!hexSeen.has(norm)) { hexSeen.add(norm); hex.push(norm); }
  }

  // Font names from the hint list (case-insensitive whole-phrase match)
  const fonts = [];
  const fontsSeen = new Set();
  for (const hint of FONT_HINTS) {
    const re = new RegExp(`\\b${hint.replace(/\s+/g, '\\s+')}\\b`, 'i');
    if (re.test(fullText) && !fontsSeen.has(hint)) {
      fontsSeen.add(hint);
      fonts.push(hint);
    }
  }

  // File paths: anything that looks like a source file. Strip surrounding
  // backticks/quotes that v0 commonly wraps paths in.
  const pathMatches = fullText.match(/[\w./-]+\.(?:tsx?|jsx?|mjs|cjs|css|s[ac]ss|html?|svg|json|md)\b/gi) || [];
  const filesSeen = new Set();
  const files = [];
  for (const p of pathMatches) {
    const clean = p.replace(/^[`'"\s]+|[`'"\s]+$/g, '');
    if (clean.length > 1 && !filesSeen.has(clean)) {
      filesSeen.add(clean);
      files.push(clean);
    }
  }

  // Revision-style responses: "You're right", "I apologize", "Let me fix", etc.
  const revisionRe = /^(you'?re right|i apologi[sz]e|sorry|let me (fix|try|recreate)|my apolog)/i;
  const revisions = changes.filter((c) => revisionRe.test(String(c.content || '').trim())).length;

  return { hex, fonts, files, revisions };
};

/**
 * Resolve a v0 chat → its branch in V0_SOURCE_REPO + the branch's current
 * commit SHA, via the GitHub API.
 *
 * Lookup strategy:
 *   1. Exact match: branch.name === chatName  (the convention v0's UI uses
 *      when both the chat and the auto-created branch share the same name)
 *   2. Fallback: branch.name contains chatId  (covers cases where the PO
 *      renamed the chat but not the branch, or vice versa)
 *
 * Returns { repo, branch, sha, status, message }
 *   status ∈ 'found' | 'not-found' | 'error'
 *   The Submission Tool writes (repo, branch, sha) to three ADO custom
 *   fields. The Spec Writer Agent reads those fields in Pass 1 to clone
 *   the exact source v0 produced at submission time.
 */
// v0 slugifies the chat name when it auto-creates the GitHub branch:
// lowercased, non-alphanumeric runs collapsed to a single hyphen, trimmed.
//   "Mybonsai v1 preview" -> "mybonsai-v1-preview"
// Branches may also carry a "v0/" prefix, stripped before comparing.
const slugifyChatName = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const stripV0Prefix = (name) => String(name || '').replace(/^v0\//, '');

const resolveV0Branch = async (chatName, chatId, githubPat) => {
  if (!chatName && !chatId) {
    return { repo: V0_SOURCE_REPO, branch: '', sha: '', status: 'error',
             branches: [], message: 'no chat name or id provided' };
  }
  try {
    const branchesUrl = `https://api.github.com/repos/${V0_SOURCE_REPO}/branches?per_page=100`;
    const res = await axios.get(branchesUrl, {
      headers: { Authorization: `token ${githubPat}`, Accept: 'application/vnd.github.v3+json' },
    });
    const branches = res.data || [];
    const branchList = branches.map((b) => ({ name: b.name, sha: b.commit?.sha || '' }));

    // Strategy 1: exact chatName ↔ branch.name match
    let match = chatName ? branches.find((b) => b.name === chatName) : null;
    let how = 'exact name match';

    // Strategy 2: slug match. v0 auto-names the branch after a slugified
    // chat name, optionally under a "v0/" prefix (e.g. chat "Mybonsai v1
    // preview" -> branch "mybonsai-v1-preview"). Compare the chat-name slug
    // against each branch name's slug, both raw and with the prefix stripped.
    if (!match && chatName) {
      const want = slugifyChatName(chatName);
      if (want) {
        match = branches.find((b) =>
          slugifyChatName(b.name) === want ||
          slugifyChatName(stripV0Prefix(b.name)) === want
        );
        how = 'slug match';
      }
    }

    // Strategy 3: branch contains chatId substring
    if (!match && chatId) {
      match = branches.find((b) => b.name.includes(chatId));
      how = 'chat-id substring match';
    }

    if (!match) {
      const sample = branches.slice(0, 5).map((b) => b.name).join(', ');
      const more = branches.length > 5 ? `, +${branches.length - 5} more` : '';
      return {
        repo: V0_SOURCE_REPO, branch: '', sha: '', status: 'not-found',
        branches: branchList,
        message:
          `No branch in ${V0_SOURCE_REPO} matches chat name "${chatName}" or chat id "${chatId}". ` +
          `Found ${branches.length} branch(es): ${sample}${more}. ` +
          `Pick the branch manually below, or connect the chat to GitHub in v0.`,
      };
    }

    return {
      repo: V0_SOURCE_REPO,
      branch: match.name,
      sha: match.commit?.sha || '',
      status: 'found',
      branches: branchList,
      message: `Resolved via ${how}.`,
    };
  } catch (err) {
    const status = err.response?.status;
    return {
      repo: V0_SOURCE_REPO, branch: '', sha: '', status: 'error',
      branches: [],
      message: `GitHub API error (HTTP ${status || '?'}): ` +
               (err.response?.data?.message || err.message),
    };
  }
};

const buildMarkdown = ({
  chatName,
  chatId,
  prototypeUrl,
  demoUrl,
  prompts,
  v0Attachments = [],
  v0AssistantChanges = [],
  v0Screenshot = null,
  screenshotPushed = false,
}) => {
  const parts = [
    `# ${chatName}`,
    '',
    `**Prototype URL:** ${prototypeUrl}`,
    '',
    `**Demo URL:** ${demoUrl || '_(not available)_'}`,
    '',
    `**Prompts:**`,
    '',
    prompts ? '```\n' + prompts + '\n```' : '_(no prompts)_',
    '',
  ];

  // ----- v0 final screenshot -----
  parts.push('## v0 final screenshot');
  parts.push('');
  if (v0Screenshot && screenshotPushed) {
    parts.push(
      'The image below is v0\'s own rendering of the final design as it ' +
      'appears in the v0 sandbox. Treat this as the authoritative pixel-level ' +
      'reference for what the React app should look like.'
    );
    parts.push('');
    parts.push(`![v0 final screenshot](assets/v0-final-screenshot.png)`);
    parts.push('');
    parts.push(`Version id: \`${v0Screenshot.versionId}\``);
  } else if (v0Screenshot) {
    parts.push('_(screenshot available from v0 API but not pushed to GitHub on this submission)_');
  } else {
    parts.push('_(no screenshot exposed by the v0 chat)_');
  }
  parts.push('');

  // ----- PO-uploaded attachments (the real design source) -----
  parts.push('## User-provided design source');
  parts.push('');
  if (v0Attachments.length > 0) {
    parts.push(
      'These are the files the PO uploaded into the v0 chat at the moments ' +
      'noted by Message #. Logos, mockup screenshots, and HTML templates ' +
      'attached here represent the visual brief the v0 assistant worked from. ' +
      'Image attachments are pushed to `assets/`, text attachments to ' +
      '`attachments/`.'
    );
    parts.push('');
    parts.push('| # | File | Type | Size | Stored at | From msg # |');
    parts.push('|---|------|------|------|-----------|------------|');
    v0Attachments.forEach((a, i) => {
      const sanitized = buildAttachmentPath(a, i);
      const subdir = a.isImage ? 'assets' : 'attachments';
      const ghPath = `prototypes/${chatId}/${subdir}/${sanitized}`;
      const sizeLabel = a.size ? `${a.size.toLocaleString()} B` : '?';
      parts.push(`| ${i + 1} | [\`${a.name}\`](${ghPath}) | ${a.contentType || '?'} | ${sizeLabel} | \`${subdir}/${sanitized}\` | ${a.messageIndex} |`);
    });
  } else {
    parts.push('_(no attachments captured - the PO did not upload any files during the v0 chat)_');
  }
  parts.push('');

  // ----- Assistant design-decisions log -----
  parts.push('## v0 design decisions log');
  parts.push('');
  if (v0AssistantChanges.length > 0) {
    parts.push(
      `The v0 assistant produced ${v0AssistantChanges.length} response(s) during this ` +
      'chat. Each one describes a change it made, often with exact hex codes, ' +
      'font names, and file paths. The full log is stored separately so it stays ' +
      'easy to grep.'
    );
    parts.push('');
    parts.push(`Full log: [\`assistant-log.md\`](assistant-log.md)`);
    parts.push('');
    parts.push('Summary (first sentence of each):');
    parts.push('');
    v0AssistantChanges.forEach((c) => {
      const firstSentence = c.content.split(/\n|(?<=[.!?])\s+/)[0].slice(0, 200).replace(/[`*_]/g, '');
      parts.push(`${c.index}. ${firstSentence}`);
    });
  } else {
    parts.push('_(no assistant responses captured)_');
  }
  parts.push('');

  return parts.join('\n');
};

const buildAdoDescription = ({ prototypeUrl, demoUrl, prompts }) =>
  `<p><strong>Prototype URL:</strong> <a href="${prototypeUrl}">${prototypeUrl}</a></p>` +
  (demoUrl ? `<p><strong>Demo URL:</strong> <a href="${demoUrl}">${demoUrl}</a></p>` : '') +
  `<p><strong>Prompts:</strong></p><pre>${prompts || '(no prompts)'}</pre>`;

/**
 * Component 3: Build the visual-inventory.md output document from the
 * /api/v0-visual-inventory response. One section per asset, with a
 * highlighted callout when the filename does not match the actual content.
 * Pass 1 reads this verbatim alongside prototype.md and assistant-log.md.
 *
 * The format is intentionally human-readable so a TL can audit the
 * mismatches manually before approving Spec Review.
 */
const buildVisualInventoryMarkdown = (inventoryResult, chatId) => {
  const result = inventoryResult || {};
  const records = Array.isArray(result.inventory) ? result.inventory : [];
  const summary = result.summary || {};
  const mismatches = records.filter((r) => r.filenameContentMismatch);

  const parts = [
    `# v0 visual inventory`,
    ``,
    `Chat: \`${chatId}\``,
    `Generated: ${new Date().toISOString()}`,
    `Total assets analyzed: ${summary.images ?? records.length}`,
    `Filename↔content mismatches flagged: ${summary.mismatchesFlagged ?? mismatches.length}`,
    `Total processing time: ${summary.totalDurationMs ?? '?'} ms`,
    ``,
    `Each image asset the PO uploaded was analyzed by Claude vision. The`,
    `\`description\`, \`contentNouns\`, and \`textExtracted\` fields describe what`,
    `the image actually CONTAINS (not what its filename implies). Use these as`,
    `the authoritative visual record when writing the spec. If \`filename↔content`,
    `mismatch\` is \`TRUE\`, the asset on disk does not match what its filename`,
    `suggests — do not reference it by the implied content.`,
    ``,
    `---`,
    ``,
  ];

  if (mismatches.length > 0) {
    parts.push(`## ⚠ Mismatches flagged (${mismatches.length})`);
    parts.push(``);
    parts.push(`These assets have filenames that do not match their actual visual content.`);
    parts.push(`The spec must reference what's visually depicted, NOT what the filename implies.`);
    parts.push(``);
    mismatches.forEach((r) => {
      parts.push(`### \`${r.name}\``);
      parts.push(``);
      parts.push(`- **Filename implies:** ${r.filenameImpliesContent || '(unknown)'}`);
      parts.push(`- **Actually depicts:** ${r.description || '(no description)'}`);
      parts.push(`- **Mismatch reason:** ${r.mismatchReason || '(not given)'}`);
      parts.push(``);
    });
    parts.push(`---`);
    parts.push(``);
  }

  parts.push(`## Per-asset inventory`);
  parts.push(``);

  if (records.length === 0) {
    parts.push(`_(no image assets analyzed — either no PO uploads or all assets failed)_`);
    parts.push(``);
    return parts.join('\n');
  }

  records.forEach((r, i) => {
    const flag = r.filenameContentMismatch ? ' — ⚠ **MISMATCH**' : '';
    parts.push(`### ${i + 1}. \`${r.name}\`${flag}`);
    parts.push(``);
    if (r.error) {
      parts.push(`> **Error:** ${r.error}`);
      parts.push(``);
      return;
    }
    // PHASE 2c polish: surface the v0-reported original name when it
    // differs from the storage filename. For v0 clipboard pastes this is
    // typically the literal string `image.png` for every asset; showing
    // it explicitly makes the index-vs-original distinction visible to
    // anyone reading the rendered markdown.
    if (r.originalName && r.originalName !== r.name) {
      parts.push(`- **v0 original filename:** \`${r.originalName}\` (stored as \`${r.name}\`)`);
    }
    if (r.url) {
      parts.push(`- **Source URL:** ${r.url.length > 80 ? r.url.slice(0, 80) + '…' : r.url}`);
    }
    if (r.contentType) {
      parts.push(`- **Content type:** \`${r.contentType}\``);
    }
    if (r.dimensions && (r.dimensions.width || r.dimensions.height)) {
      parts.push(`- **Dimensions:** ${r.dimensions.width || '?'} × ${r.dimensions.height || '?'} px`);
    }
    if (Array.isArray(r.dominantColors) && r.dominantColors.length > 0) {
      parts.push(`- **Dominant colours:** ${r.dominantColors.map((c) => `\`${c}\``).join(', ')}`);
    }
    parts.push(`- **Description:** ${r.description || '_(none)_'}`);
    if (Array.isArray(r.contentNouns) && r.contentNouns.length > 0) {
      parts.push(`- **Content nouns:** ${r.contentNouns.map((n) => `\`${n}\``).join(', ')}`);
    }
    if (r.textExtracted) {
      parts.push(`- **Text in image:** "${r.textExtracted}"`);
    }
    if (r.filenameImpliesContent) {
      parts.push(`- **Filename implies:** ${r.filenameImpliesContent}`);
    }
    parts.push(`- **Filename↔content mismatch:** ${r.filenameContentMismatch ? '`true`' : '`false`'}`);
    if (r.filenameContentMismatch && r.mismatchReason) {
      parts.push(`- **Mismatch reason:** ${r.mismatchReason}`);
    }
    parts.push(`- **Vision call time:** ${r.claudeDurationMs ?? '?'} ms`);
    parts.push(``);
  });

  return parts.join('\n');
};

const isMissingFieldError = (err) => {
  const msg = err?.response?.data?.message || '';
  return (
    err?.response?.status === 400 &&
    (msg.includes('Cannot find field') || msg.includes('TF51535'))
  );
};

const bonsaiTitle = (id, featureName) => `BONSAI-${id}: ${featureName}`;

// ── UI Components ─────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  return (
    <Chip
      label={status}
      color={STATUS_COLOR[status] || 'default'}
      size="small"
      sx={{ ml: 1, verticalAlign: 'middle', fontWeight: 600 }}
    />
  );
}

function StepCard({ title, children }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>{title}</Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

// Full-screen loading shown while MSAL is signing the user in.
function AuthLoadingScreen({ message }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 2,
        alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: '#fafafa',
      }}>
        <Box component="img" src="/mybonsai-logo.svg" alt="MyBonsai" sx={{ height: 56, mb: 1 }} />
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">{message}</Typography>
      </Box>
    </ThemeProvider>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  // ── MSAL auth (works identically on localhost and on deployed SWA) ─────────
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  const isAuthenticated = useIsAuthenticated();
  // Entra returns the user's UPN/email in account.username for work accounts
  const userEmail = account?.username || account?.idTokenClaims?.email || null;
  const userName  = account?.name || userEmail || '';

  // ── PO selection ───────────────────────────────────────────────────────────
  const [selectedPO, setSelectedPO] = useState(() => {
    const stored = localStorage.getItem(PO_STORAGE_KEY) || '';
    return AVAILABLE_POS.includes(stored) ? stored : '';
  });
  const activeV0Key = useMemo(() => getV0KeyForPO(selectedPO), [selectedPO]);
  const selectedPOMissingKey = !!(
    selectedPO && !V0_KEYS_BY_SUFFIX[getKeySuffix(selectedPO)]
  );

  // ── Step 1 ─────────────────────────────────────────────────────────────────
  const [projectGroups, setProjectGroups]     = useState([]);
  const [standaloneChats, setStandaloneChats] = useState([]);
  const [selectedChatId, setSelectedChatId]   = useState('');
  const [selectedChatObj, setSelectedChatObj] = useState(null);
  const [step1Status, setStep1Status]         = useState('idle');

  // ── Step 2 ─────────────────────────────────────────────────────────────────
  const [prototypeUrl, setPrototypeUrl] = useState('');
  const [demoUrl, setDemoUrl]           = useState('');
  const [prompts, setPrompts]           = useState('');
  const [promptsEmpty, setPromptsEmpty] = useState(false);
  const [storyTitle, setStoryTitle]     = useState('');
  const [step2Status, setStep2Status]   = useState('idle');
  // v0 capture state — populated by extractV0Attachments / extractAssistantChanges /
  // extractV0Screenshot from the chat-detail response. NOTE: the actual v0 source
  // code is NOT captured here. It lives in `Bonsai-LLC/mybonsai_version_1` via v0's
  // GitHub integration; the Submission Tool records the binding (repo/branch/sha)
  // in ADO custom fields and the Spec Writer Agent clones from there in Pass 1.
  const [v0Attachments, setV0Attachments]           = useState([]);
  const [v0AssistantChanges, setV0AssistantChanges] = useState([]);
  const [v0Screenshot, setV0Screenshot]             = useState(null);
  // Full chat-detail JSON kept around so Step 3 can re-scan it for
  // additional screenshot version candidates (latestVersion may be a
  // blank placeholder when the PO's last move was a code export; we walk
  // back through versions in pushToGitHub to find a usable render).
  const [v0ChatDetail, setV0ChatDetail]             = useState(null);

  // Component 1 — v0 → GitHub source binding. Resolved automatically in
  // fetchPrototype after the chat detail loads, then displayed in the v0
  // Design Capture card and written to three ADO custom fields (with a
  // structured-comment fallback if the fields don't exist yet) in Step 4.
  const [v0SourceRepo, setV0SourceRepo]       = useState(V0_SOURCE_REPO);
  const [v0SourceBranch, setV0SourceBranch]   = useState('');
  const [v0SourceSha, setV0SourceSha]         = useState('');
  const [v0SourceStatus, setV0SourceStatus]   = useState('idle'); // idle|searching|found|not-found|error
  const [v0SourceMessage, setV0SourceMessage] = useState('');
  const [v0Branches, setV0Branches]           = useState([]); // branch list for the manual-pick fallback

  // ── Step 3 ─────────────────────────────────────────────────────────────────
  const [commitSha, setCommitSha]     = useState('');
  const [githubUrl, setGithubUrl]     = useState('');
  const [step3Status, setStep3Status] = useState('idle');

  // ── Step 4 ─────────────────────────────────────────────────────────────────
  const [workItemType, setWorkItemType]               = useState('User Story');
  const [adoUrl, setAdoUrl]                           = useState('');
  const [adoTicketId, setAdoTicketId]                 = useState(null);
  const [step4Status, setStep4Status]                 = useState('idle');
  const [customFieldsAvailable, setCustomFieldsAvailable] = useState(null);

  // Last successful submission, persisted to localStorage so the PO can see
  // a clear reference after page reload and so the banner survives reset.
  const [lastSubmission, setLastSubmission] = useState(() => {
    try {
      const stored = localStorage.getItem(LAST_SUBMISSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // ── Debug ──────────────────────────────────────────────────────────────────
  const [log, setLog]                   = useState([]);
  const [rawResponses, setRawResponses] = useState({});
  const [copiedKey, setCopiedKey]       = useState('');

  const addLog  = useCallback((msg) => setLog((l) => [...l, `[${new Date().toLocaleTimeString()}] ${msg}`]), []);
  const saveRaw = useCallback((key, data) => setRawResponses((r) => ({ ...r, [key]: data })), []);
  const handleCopy = useCallback((key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(''), 2000);
  }, []);

  // ── Force login if not authenticated ───────────────────────────────────────
  // Only triggers loginRedirect once MSAL has finished its own startup work
  // (inProgress === None) AND the user has no active account. This prevents
  // racing against MSAL's cached-account lookup or in-flight redirect handling.
  useEffect(() => {
    if (inProgress === InteractionStatus.None && !isAuthenticated) {
      instance.loginRedirect(loginRequest).catch((err) => {
        console.error('[auth] loginRedirect failed:', err);
      });
    }
  }, [inProgress, isAuthenticated, instance]);

  // ── Auto-select PO based on signed-in email ───────────────────────────────
  // Only sets if no PO is already in localStorage so manual overrides stick.
  useEffect(() => {
    if (!userEmail) return;
    addLog(`Signed in: ${userEmail}`);
    const persisted = localStorage.getItem(PO_STORAGE_KEY);
    if (persisted) return;
    const matched = matchPOFromEmail(userEmail, AVAILABLE_POS);
    if (matched) {
      setSelectedPO(matched);
      localStorage.setItem(PO_STORAGE_KEY, matched);
      addLog(`Auto-selected PO from email: ${matched}`);
    } else {
      addLog('Email did not match any PO — pick manually if needed.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // ── PO change handler ──────────────────────────────────────────────────────
  const handlePOChange = useCallback((e) => {
    const po = e.target.value;
    setSelectedPO(po);
    if (po) localStorage.setItem(PO_STORAGE_KEY, po);
    else    localStorage.removeItem(PO_STORAGE_KEY);

    setProjectGroups([]);
    setStandaloneChats([]);
    setSelectedChatId('');
    setSelectedChatObj(null);
    setPrototypeUrl('');
    setDemoUrl('');
    setPrompts('');
    setPromptsEmpty(false);
    setStoryTitle('');
    setStep2Status('idle');
    setV0Attachments([]);
    setV0AssistantChanges([]);
    setV0Screenshot(null);
    setV0ChatDetail(null);
    setV0SourceRepo(V0_SOURCE_REPO);
    setV0SourceBranch('');
    setV0SourceSha('');
    setV0SourceStatus('idle');
    setV0SourceMessage('');
    setV0Branches([]);
    addLog(po ? `Switched PO to "${po}".` : 'Cleared PO (using legacy default key).');
  }, [addLog]);

  // ── Sign out handler ───────────────────────────────────────────────────────
  // Reset all per-submission state for a fresh ticket. Deliberately keeps:
  //   - selectedPO        (persistent across submissions)
  //   - projectGroups + standaloneChats (expensive to re-fetch, still valid)
  //   - log + rawResponses (debug history)
  //   - lastSubmission    (banner reference)
  const resetForm = useCallback(() => {
    setSelectedChatId('');
    setSelectedChatObj(null);
    setPrototypeUrl('');
    setDemoUrl('');
    setPrompts('');
    setPromptsEmpty(false);
    setStoryTitle('');
    setStep2Status('idle');
    setV0Attachments([]);
    setV0AssistantChanges([]);
    setV0Screenshot(null);
    setV0ChatDetail(null);
    setV0SourceRepo(V0_SOURCE_REPO);
    setV0SourceBranch('');
    setV0SourceSha('');
    setV0SourceStatus('idle');
    setV0SourceMessage('');
    setV0Branches([]);
    setCommitSha('');
    setGithubUrl('');
    setStep3Status('idle');
    setAdoUrl('');
    setAdoTicketId(null);
    setStep4Status('idle');
    addLog('Form reset for new submission.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [addLog]);

  const handleSignOut = useCallback(() => {
    // Clear local PO selection too so the next sign-in re-evaluates from email
    localStorage.removeItem(PO_STORAGE_KEY);
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  // ── Step 1: fetch v0 projects + chats ─────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated) return; // don't fetch before login completes
    setStep1Status('loading');
    addLog(`Fetching v0.dev projects and chats${selectedPO ? ` (as ${selectedPO})` : ''}…`);
    const headers = { Authorization: `Bearer ${activeV0Key}` };

    const [projResult, chatsResult] = await Promise.allSettled([
      axios.get('https://api.v0.dev/v1/projects', { headers }),
      axios.get('https://api.v0.dev/v1/chats', { headers }),
    ]);

    let groups = [];

    if (projResult.status === 'fulfilled') {
      const r = projResult.value;
      saveRaw('projects', r.data);
      addLog(`GET /v1/projects → HTTP ${r.status}`);
      groups = toArray(r.data).map((p) => ({
        id: p.id,
        name: p.name || p.title || p.id,
        chats: toArray(p.chats || p.threads || []),
      }));
      addLog(`Projects: ${groups.length} found.`);
    } else {
      const err = projResult.reason;
      saveRaw('projects_error', err.response?.data || err.message);
      addLog(`GET /v1/projects → ${err.response?.status ?? 'error'}: ${JSON.stringify(err.response?.data || err.message)}`);
    }

    if (chatsResult.status === 'fulfilled') {
      const r = chatsResult.value;
      saveRaw('chats', r.data);
      addLog(`GET /v1/chats → HTTP ${r.status}`);
      const allChats = toArray(r.data);
      const projectChatIds = new Set(groups.flatMap((g) => g.chats.map((c) => c.id)));
      const standalone = allChats.filter((c) => !c.projectId && !projectChatIds.has(c.id));
      setStandaloneChats(standalone);
      addLog(`Chats: ${allChats.length} total, ${standalone.length} standalone.`);
    } else {
      const err = chatsResult.reason;
      saveRaw('chats_error', err.response?.data || err.message);
      addLog(`GET /v1/chats → ${err.response?.status ?? 'error'}: ${JSON.stringify(err.response?.data || err.message)}`);
      setStandaloneChats([]);
    }

    setProjectGroups(groups);
    setStep1Status('success');
  }, [addLog, saveRaw, activeV0Key, selectedPO, isAuthenticated]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // ── Step 2: fetch chat messages / prompts ──────────────────────────────────
  const fetchPrototype = useCallback(async (chatId, chatObj) => {
    if (!chatId) return;
    setStep2Status('loading');
    addLog(`Fetching details for chat: ${chatId}`);

    const headers = { Authorization: `Bearer ${activeV0Key}` };

    // Prototype URL is reliable from the list-endpoint object.
    const proto = chatObj?.webUrl || `https://v0.dev/chat/${chatId}`;

    // Demo URL is NOT in the list-endpoint response — v0.dev returns chat
    // summaries there without latestVersion.demoUrl. We always need to call
    // the detail endpoint `GET /v1/chats/:id` to get it. Try multiple known
    // field paths so a v0.dev API shape change doesn't silently empty the
    // field again.
    let demo = chatObj?.latestVersion?.demoUrl || '';
    if (!demo) {
      try {
        addLog(`Fetching chat detail for demo URL: GET /v1/chats/${chatId}`);
        const detailRes = await axios.get(`https://api.v0.dev/v1/chats/${chatId}`, { headers });
        saveRaw('chat_detail', detailRes.data);
        const d = detailRes.data || {};
        demo =
          d?.latestVersion?.demoUrl ||
          d?.latestVersion?.demo ||
          d?.demoUrl ||
          d?.demo?.url ||
          d?.preview?.url ||
          d?.previewUrl ||
          '';
        addLog(
          demo
            ? `Got demo URL from chat detail (${demo.length} chars)`
            : 'Chat detail returned no demo URL — v0 may not have rendered a sandbox yet'
        );
      } catch (err) {
        saveRaw('chat_detail_error', err.response?.data || err.message);
        addLog(
          `Chat detail fetch failed: ${err.response?.status ?? 'error'} — demo URL will be left blank (you can paste it manually)`
        );
      }
    }

    setPrototypeUrl(proto);
    setDemoUrl(demo);

    // Fetch chat detail — used below by extractV0Attachments, extractAssistantChanges,
    // and extractV0Screenshot to recover the PO uploads, the assistant decision log, and
    // v0's own screenshot URL. (v0's generated source code is NOT in this response —
    // it lives in the v0→GitHub-connected repo, resolved separately at submission time.)
    let chatDetailForFiles = null;
    try {
      const detailRes2 = await axios.get(`https://api.v0.dev/v1/chats/${chatId}`, { headers });
      saveRaw('chat_detail', detailRes2.data);
      chatDetailForFiles = detailRes2.data;
    } catch (err) {
      addLog(`chat-detail fetch failed (${err.response?.status ?? err.message}) — attachments/screenshot/log will be empty`);
    }
    // Extract the three real design sources from the chat-detail response:
    // user attachments, assistant decisions log, and the v0 screenshot URL.
    // These give Pass 1 visual grounding (the BlackPine HTML, the logo PNG,
    // exact hex codes from assistant replies, and v0's own final rendering).
    const extractedAttachments = chatDetailForFiles ? extractV0Attachments(chatDetailForFiles) : [];
    const extractedChanges     = chatDetailForFiles ? extractAssistantChanges(chatDetailForFiles) : [];
    const extractedScreenshot  = chatDetailForFiles ? extractV0Screenshot(chatDetailForFiles) : null;
    setV0Attachments(extractedAttachments);
    setV0AssistantChanges(extractedChanges);
    setV0Screenshot(extractedScreenshot);
    setV0ChatDetail(chatDetailForFiles);

    if (extractedAttachments.length > 0) {
      const imgCount = extractedAttachments.filter((a) => a.isImage).length;
      const txtCount = extractedAttachments.filter((a) => a.isText).length;
      addLog(
        `Captured ${extractedAttachments.length} PO attachment(s) — ${imgCount} image, ${txtCount} text`
      );
      extractedAttachments.forEach((a) => {
        addLog(`  · ${a.name} (${a.size} B, ${a.contentType || 'unknown'}, msg #${a.messageIndex})`);
      });
    } else {
      addLog('No PO-uploaded attachments found in this chat.');
    }
    if (extractedChanges.length > 0) {
      addLog(`Captured ${extractedChanges.length} assistant design-decision message(s).`);
    } else {
      addLog('No assistant decision-log messages captured.');
    }
    if (extractedScreenshot) {
      addLog(`v0 final screenshot available (version ${extractedScreenshot.versionId}); will be fetched on Step 3.`);
    } else {
      addLog('No v0 screenshotUrl in this chat.');
    }

    try {
      const r = await axios.get(`https://api.v0.dev/v1/chats/${chatId}/messages`, { headers });
      saveRaw('messages', r.data);
      addLog(`GET /chats/${chatId}/messages → HTTP ${r.status}, ${toArray(r.data).length} messages`);

      const msgs = toArray(r.data);
      const promptText = formatPrompts(msgs);
      const promptCount = promptText ? (promptText.match(/^\[\d+\]/gm) || []).length : 0;

      const isEmpty = !promptText;
      setPromptsEmpty(isEmpty);
      setPrompts(promptText);
      addLog(isEmpty ? 'No user prompts found.' : `Extracted ${promptCount} prompt(s), ${promptText.length} chars.`);
    } catch (err) {
      saveRaw('messages_error', err.response?.data || err.message);
      addLog(`messages → ${err.response?.status ?? 'error'}: ${JSON.stringify(err.response?.data || err.message)}`);
      setPrompts('');
      setPromptsEmpty(false);
      setStep2Status('error');
      return;
    }

    const chatName = chatObj?.name || chatObj?.title || chatId;
    setStoryTitle(chatName);

    // Component 1: resolve the v0 → GitHub source binding before we mark
    // Step 2 done. Blocking on this keeps the v0 Design Capture card from
    // flashing "searching…" then "found" — by the time the card appears
    // the binding is either resolved or we know why it failed.
    setV0SourceStatus('searching');
    addLog(`Resolving v0 source binding in ${V0_SOURCE_REPO}…`);
    const binding = await resolveV0Branch(chatName, chatId, GITHUB_PAT);
    setV0SourceRepo(binding.repo);
    setV0SourceBranch(binding.branch);
    setV0SourceSha(binding.sha);
    setV0SourceStatus(binding.status);
    setV0SourceMessage(binding.message);
    setV0Branches(binding.branches || []);
    if (binding.status === 'found') {
      addLog(`✓ v0 source: ${binding.branch}@${binding.sha.slice(0, 7)} (${binding.message})`);
    } else {
      addLog(`⚠ v0 source: ${binding.message}`);
    }

    setStep2Status('success');
    addLog('Prototype details loaded.');
  }, [addLog, saveRaw, activeV0Key]);

  // Manual branch picker fallback: when auto-resolution can't match the
  // chat to a branch (v0's branch naming is sometimes opaque, e.g.
  // "v0/branch-preview-*"), the user selects the branch here. We write the
  // picked branch + its HEAD sha and flip status to 'found' so Step 4
  // writes the V0Source custom fields.
  const handleManualBranchSelect = useCallback((branchName) => {
    const picked = v0Branches.find((b) => b.name === branchName);
    if (!picked) return;
    setV0SourceBranch(picked.name);
    setV0SourceSha(picked.sha || '');
    setV0SourceStatus('found');
    setV0SourceMessage(`Branch selected manually: ${picked.name}`);
    addLog(`v0 source: manually selected branch ${picked.name}@${(picked.sha || '').slice(0, 7)}`);
  }, [v0Branches, addLog]);

  const handleChatSelect = useCallback((e) => {
    const chatId = e.target.value;
    setSelectedChatId(chatId);
    if (!chatId) {
      setSelectedChatObj(null);
      setPrototypeUrl('');
      setDemoUrl('');
      setPrompts('');
      setPromptsEmpty(false);
      setStoryTitle('');
      setStep2Status('idle');
      setV0Attachments([]);
      setV0AssistantChanges([]);
      setV0Screenshot(null);
      setV0ChatDetail(null);
      setV0SourceRepo(V0_SOURCE_REPO);
      setV0SourceBranch('');
      setV0SourceSha('');
      setV0SourceStatus('idle');
      setV0SourceMessage('');
      setV0Branches([]);
      return;
    }
    let found = null;
    for (const proj of projectGroups) {
      found = proj.chats.find((c) => c.id === chatId);
      if (found) break;
    }
    if (!found) found = standaloneChats.find((c) => c.id === chatId);
    setSelectedChatObj(found || null);
    fetchPrototype(chatId, found || null);
  }, [projectGroups, standaloneChats, fetchPrototype]);

  // ── Step 3: push prototype.md to GitHub ───────────────────────────────────
  const pushToGitHub = async () => {
    setStep3Status('loading');
    const chatName = selectedChatObj?.name || selectedChatObj?.title || selectedChatId;
    const folder   = selectedChatId || 'prototype';
    const filePath = `prototypes/${folder}/prototype.md`;
    addLog(`Pushing ${filePath} to GitHub…`);

    // Helper: idempotent PUT to GitHub Contents API. Looks up existing sha
    // first so we don't error on re-submissions for the same chat.
    const putContent = async (ghPath, base64Content, commitMsg) => {
      let existingSha = '';
      try {
        const check = await axios.get(
          `https://api.github.com/repos/${GITHUB_OWNER}/${GITHUB_REPO}/contents/${ghPath}`,
          { headers: githubHeaders }
        );
        existingSha = check.data.sha;
      } catch (_) { /* doesn't exist yet, first push */ }
      return axios.put(
        `https://api.github.com/repos/${GITHUB_OWNER}/${GITHUB_REPO}/contents/${ghPath}`,
        { message: commitMsg, content: base64Content, ...(existingSha ? { sha: existingSha } : {}) },
        { headers: githubHeaders }
      );
    };

    try {
      // PHASE 1.2a: Push PO-uploaded attachments (images to assets/, text to
      // attachments/). These are the logos, mockup screenshots, and HTML
      // templates the PO uploaded into the v0 chat — the real design source.
      //
      // Filenames are index-prefixed (01-, 02-, ...) because v0 reports the
      // same `name` for every clipboard-pasted image. Without the prefix,
      // all four uploads would collide on `image.png` and only the last
      // would survive.
      const pushedAttachments = [];
      const skippedAttachments = [];
      for (const [i, a] of v0Attachments.entries()) {
        const sanitized = buildAttachmentPath(a, i);
        if (!sanitized) {
          skippedAttachments.push({ name: a.name, reason: 'invalid path' });
          continue;
        }
        if (a.size > 900_000) {
          skippedAttachments.push({ name: a.name, reason: `too large (${a.size}B)` });
          continue;
        }
        const subdir = a.isImage ? 'assets' : 'attachments';
        const ghPath = `prototypes/${folder}/${subdir}/${sanitized}`;
        try {
          const content64 = await fetchAsBase64(a.url);
          await putContent(ghPath, content64, `feat(${folder}): v0 ${subdir} ${sanitized}`);
          pushedAttachments.push({ name: sanitized, originalName: a.name, contentType: a.contentType, size: a.size, subdir });
          addLog(`  → pushed ${subdir}/${sanitized} (${a.size} B, ${a.contentType})`);
        } catch (err) {
          const reason = err.response?.status ? `HTTP ${err.response.status}` : err.message;
          skippedAttachments.push({ name: a.name, reason });
          addLog(`  ✗ failed to push attachment ${sanitized}: ${reason}`);
        }
      }
      saveRaw('v0_attachments_push', { pushed: pushedAttachments, skipped: skippedAttachments });
      if (pushedAttachments.length > 0) {
        addLog(`Pushed ${pushedAttachments.length} PO attachment(s).`);
      }

      // PHASE 1.2b: Push v0's screenshot, auto-walking versions to find
      // the first non-blank render. Chats where the PO's last user turn
      // was an HTML/code export end up with a blank latestVersion
      // screenshot — we walk back through earlier UI-iteration versions
      // to find a render above the 20 KB blank threshold.
      let screenshotPushed = false;
      let pickedScreenshotInfo = null;
      if (activeV0Key && v0ChatDetail) {
        const candidates = extractV0ScreenshotCandidates(v0ChatDetail);
        if (candidates.length === 0) {
          addLog('No v0 screenshot URLs exposed in this chat.');
        } else {
          addLog(`Found ${candidates.length} v0 screenshot candidate(s) — walking for the first non-blank render…`);
          const picked = await findUsableV0Screenshot(candidates, activeV0Key, addLog);
          if (picked) {
            try {
              const ghPath = `prototypes/${folder}/assets/v0-final-screenshot.png`;
              await putContent(ghPath, picked.base64, `feat(${folder}): v0 screenshot from ${picked.label}`);
              screenshotPushed = true;
              pickedScreenshotInfo = picked;
              addLog(`  → pushed v0 screenshot to ${ghPath}`);
            } catch (err) {
              const reason = err.response?.status ? `HTTP ${err.response.status}` : err.message;
              addLog(`  ✗ failed to push v0 screenshot: ${reason}`);
            }
          } else {
            addLog(`  ✗ all ${candidates.length} candidate(s) returned blank or failed — Pass 1 will rely on PO attachments instead.`);
          }
        }
      } else if (!activeV0Key) {
        addLog('No v0 API key available — skipping screenshot fetch.');
      }
      saveRaw('v0_screenshot_push', {
        pushed: screenshotPushed,
        versionId: pickedScreenshotInfo?.versionId || null,
        sourceLabel: pickedScreenshotInfo?.label || null,
        sizeBytes: pickedScreenshotInfo?.size || null,
      });

      // PHASE 1.2c: Push assistant decisions log as a standalone markdown
      // file so Pass 1 can read it without re-parsing the chat detail.
      let assistantLogPushed = false;
      if (v0AssistantChanges.length > 0) {
        const logMd = [
          `# v0 design decisions log`,
          ``,
          `Chat: \`${selectedChatId}\``,
          `Captured: ${new Date().toISOString()}`,
          `Total assistant responses: ${v0AssistantChanges.length}`,
          ``,
          `Each section below is one assistant response in chronological order.`,
          `Hex codes, font names, and file paths mentioned in these responses are`,
          `the authoritative record of the design decisions v0 made on behalf of`,
          `the PO.`,
          ``,
          `---`,
          ``,
          ...v0AssistantChanges.map((c) => [
            `## Decision ${c.index}${c.createdAt ? ` (${c.createdAt})` : ''}`,
            ``,
            c.content,
            ``,
          ].join('\n')),
        ].join('\n');
        try {
          const ghPath = `prototypes/${folder}/assistant-log.md`;
          const content64 = btoa(unescape(encodeURIComponent(logMd)));
          await putContent(ghPath, content64, `feat(${folder}): v0 assistant decisions log`);
          assistantLogPushed = true;
          addLog(`  → pushed assistant log (${v0AssistantChanges.length} entries) to ${ghPath}`);
        } catch (err) {
          const reason = err.response?.status ? `HTTP ${err.response.status}` : err.message;
          addLog(`  ✗ failed to push assistant log: ${reason}`);
        }
      }
      saveRaw('v0_assistant_log_push', { pushed: assistantLogPushed, count: v0AssistantChanges.length });

      // PHASE 1.2d (Component 3): Visual Inventory Service. Run Claude vision
      // on every image asset to record what each one ACTUALLY depicts (not
      // what its filename implies). Output is pushed alongside prototype.md
      // as visual-inventory.md, consumed by Pass 1 to ground spec writing on
      // visual truth — closing Q1 (the BERRY logo class of regressions).
      //
      // Architecture: `.specify/memory/permanent-factory-architecture.md`
      //               (Component 3) in mybonsai_react_app.
      //
      // PHASE 2c polish: We pre-compute the sanitized STORAGE filename for
      // each image and send it as `name` to the inventory API. v0 reports
      // every clipboard-pasted image with the literal name "image.png", so
      // without this, the rendered inventory.md showed
      //   `### 1. image.png` ... `### 5. image.png`
      // and downstream consumers (Pass 2, TLs) had no way to map
      // "inventory item N" back to the indexed GitHub storage filename. That's
      // what produced the BONSAI-36 04 vs 05 confusion. Sending the sanitized
      // name fixes the rendering AND makes Component 6's manifest ↔ inventory
      // join key (`r.name === sanitized`) actually match.
      let visualInventoryPushed = false;
      let visualInventoryResult = null;
      const imageAttachmentsForInventory = v0Attachments
        .map((a, i) => ({ ...a, _storageFilename: buildAttachmentPath(a, i) }))
        .filter((a) => a.isImage);
      if (imageAttachmentsForInventory.length > 0) {
        addLog(`Running visual inventory on ${imageAttachmentsForInventory.length} image asset(s) via Claude vision…`);
        try {
          const invRes = await axios.post('/api/v0-visual-inventory', {
            chatId: folder,
            assets: imageAttachmentsForInventory.map((a) => ({
              // `name` is the SANITIZED storage filename (e.g. "05-image.png")
              // — what the file is actually called in GitHub. Downstream uses
              // this as the join key into the asset-manifest.json bindings.
              name: a._storageFilename,
              // `originalName` preserves v0's reported name (typically just
              // "image.png" for clipboard pastes) for human audit — surfaced
              // in the rendered inventory.md when it differs from `name`.
              originalName: a.name,
              url: a.url,
              contentType: a.contentType,
            })),
          }, {
            // Vision calls are slow (~3-6s each, parallel batches of 3). Allow 90s
            // for a typical PO upload set of 1-8 images.
            timeout: 90_000,
          });
          visualInventoryResult = invRes.data;
          saveRaw('v0_visual_inventory', visualInventoryResult);
          const sum = visualInventoryResult?.summary || {};
          addLog(
            `  ✓ inventory: ${sum.processed || 0}/${sum.images || 0} images processed, ` +
            `${sum.mismatchesFlagged || 0} filename↔content mismatches flagged ` +
            `(${sum.totalDurationMs || 0} ms)`
          );
          if ((sum.mismatchesFlagged || 0) > 0) {
            (visualInventoryResult.inventory || []).forEach((r) => {
              if (r.filenameContentMismatch) {
                addLog(`    ⚠ MISMATCH: ${r.name} — ${r.mismatchReason || 'see inventory.md'}`);
              }
            });
          }
          // Build the markdown report and push it
          const inventoryMd = buildVisualInventoryMarkdown(visualInventoryResult, folder);
          const invGhPath = `prototypes/${folder}/visual-inventory.md`;
          const inventoryContent = btoa(unescape(encodeURIComponent(inventoryMd)));
          await putContent(invGhPath, inventoryContent, `feat(${folder}): v0 visual inventory`);
          visualInventoryPushed = true;
          addLog(`  → pushed visual-inventory.md to ${invGhPath}`);
        } catch (err) {
          const reason = err.response?.data?.error || err.message;
          addLog(`  ✗ visual inventory failed: ${reason} — proceeding (Pass 1 will fall back to filename-based heuristics)`);
          saveRaw('v0_visual_inventory_error', err.response?.data || err.message);
        }
      } else {
        addLog('Skipping visual inventory: no image attachments to analyze.');
      }
      saveRaw('v0_visual_inventory_push', { pushed: visualInventoryPushed, mismatches: visualInventoryResult?.summary?.mismatchesFlagged || 0 });

      // PHASE 2c (Component 6): Asset Manifest — single source of truth
      // binding CDN-URL ↔ storage-filename ↔ semantic-description.
      //
      // Why this exists: the Spec Writer Agent has to emit `copy_asset`
      // source paths in tasks.md. Without an explicit binding, Pass 2 had
      // to match v0 source CDN URLs against PO uploads by description
      // (via visual-inventory.md), which is unreliable: two PO-uploaded
      // images can produce similar Claude-vision descriptions, leading
      // to mis-selected indices (BONSAI-36 picked 04-image.png when the
      // v0 source's only CDN URL was bound to 05-image.png).
      //
      // The manifest provides a DETERMINISTIC binding chain:
      //   v0 source `<Image src="...CDN_URL...">`
      //   → manifest.assets[].cdnUrl === CDN_URL
      //   → manifest.assets[].storedAs === actual GitHub storage path
      //
      // Pass 1 of the Spec Writer scans the cloned v0 source for CDN
      // URLs, cross-references this manifest, and writes the resulting
      // bindings to `.specify/specs/BONSAI-{id}/asset-bindings.md` on
      // the spec branch. Pass 2 reads that file as the AUTHORITATIVE
      // source for `copy_asset.source` paths. AI Runner pre-flight
      // cross-checks each task's source against the bindings before
      // claiming the work item.
      //
      // The manifest combines data we already collected: v0Attachments
      // (PO uploads with original CDN URLs), pushedAttachments (what
      // actually landed in GitHub and at which storage path), and
      // visualInventoryResult (Claude-vision semantic data for each
      // image). Idempotent: re-running Step 3 against the same chat
      // overwrites the manifest with current state.
      let assetManifestPushed = false;
      try {
        const manifestAssets = v0Attachments.map((a, i) => {
          const sanitized = buildAttachmentPath(a, i);
          const subdir = a.isImage ? 'assets' : 'attachments';
          const storedAs = `prototypes/${folder}/${subdir}/${sanitized}`;
          // Match against inventory by storage-filename (the inventory's
          // `name` field is the sanitized filename the Submission Tool
          // produced — same one we compute here via buildAttachmentPath).
          const inventoryRecord = (visualInventoryResult?.inventory || [])
            .find((r) => r.name === sanitized);
          // Whether the push actually succeeded (PUT to GitHub returned
          // 2xx). pushedAttachments was built earlier in this function
          // by the per-attachment loop; we look up by sanitized name
          // because the loop tracked the same identifier.
          const wasPushed = pushedAttachments.some((p) => p.name === sanitized);
          return {
            id: `asset-${String(i + 1).padStart(2, '0')}`,
            storedAs,
            cdnUrl: a.url,
            originalName: a.name,
            sanitizedName: sanitized,
            subdir,
            size: a.size || 0,
            contentType: a.contentType || null,
            messageIndex: typeof a.messageIndex === 'number' ? a.messageIndex : null,
            messageRole: a.messageRole || null,
            isImage: !!a.isImage,
            isText: !!a.isText,
            pushedToGitHub: wasPushed,
            semanticDescription: inventoryRecord?.description || null,
            contentNouns: Array.isArray(inventoryRecord?.contentNouns) ? inventoryRecord.contentNouns : [],
            filenameImpliesContent: inventoryRecord?.filenameImpliesContent || null,
            filenameContentMismatch: !!inventoryRecord?.filenameContentMismatch,
            mismatchReason: inventoryRecord?.mismatchReason || null,
            extractedText: inventoryRecord?.textExtracted || null,
          };
        });
        // v0 screenshot is treated as a special asset. Its CDN URL is not
        // exposed (the screenshot is proxied through the SWA backend
        // because v0's endpoint requires Bearer auth that the browser
        // can't send), so cdnUrl is null and downstream lookups by CDN
        // URL won't match it — but its `storedAs` is well-known and
        // downstream consumers can reference it by id `v0-screenshot`.
        if (screenshotPushed && pickedScreenshotInfo) {
          manifestAssets.push({
            id: 'v0-screenshot',
            storedAs: `prototypes/${folder}/assets/v0-final-screenshot.png`,
            cdnUrl: null,
            originalName: 'v0-final-screenshot.png',
            sanitizedName: 'v0-final-screenshot.png',
            subdir: 'assets',
            size: pickedScreenshotInfo.size || 0,
            contentType: pickedScreenshotInfo.contentType || 'image/png',
            messageIndex: null,
            messageRole: null,
            isImage: true,
            isText: false,
            pushedToGitHub: true,
            semanticDescription: `v0 final rendering (source: ${pickedScreenshotInfo.label || 'latestVersion'})`,
            contentNouns: ['screenshot', 'v0-render'],
            filenameImpliesContent: null,
            filenameContentMismatch: false,
            mismatchReason: null,
            extractedText: null,
          });
        }
        const manifest = {
          schemaVersion: 1,
          chatId: folder,
          generatedAt: new Date().toISOString(),
          generatedBy: 'mybonsai-submission-tool (Step 3, Component 6)',
          totalAssets: manifestAssets.length,
          assets: manifestAssets,
        };
        const manifestJson = JSON.stringify(manifest, null, 2);
        const manifestPath = `prototypes/${folder}/asset-manifest.json`;
        const manifestB64 = btoa(unescape(encodeURIComponent(manifestJson)));
        await putContent(manifestPath, manifestB64, `feat(${folder}): asset manifest (Component 6 binding chain)`);
        assetManifestPushed = true;
        addLog(`  → pushed asset-manifest.json (${manifestAssets.length} asset[s]) to ${manifestPath}`);
      } catch (err) {
        const reason = err.response?.status ? `HTTP ${err.response.status}` : err.message;
        addLog(`  ✗ failed to push asset-manifest.json: ${reason} — Pass 1 will fall back to description-matching for copy_asset paths (less reliable)`);
        saveRaw('v0_asset_manifest_error', err.response?.data || err.message);
      }
      saveRaw('v0_asset_manifest_push', { pushed: assetManifestPushed });

      const markdown = buildMarkdown({
        chatName,
        chatId: folder,
        prototypeUrl,
        demoUrl,
        prompts,
        v0Attachments,
        v0AssistantChanges,
        v0Screenshot,
        screenshotPushed,
      });
      const content = btoa(unescape(encodeURIComponent(markdown)));
      const res = await putContent(filePath, content, `feat: add prototype ${folder}`);
      saveRaw('github', res.data);
      const sha = res.data.commit?.sha || '';
      setCommitSha(sha);
      setGithubUrl(res.data.content?.html_url || '');
      setStep3Status('success');
      addLog(`GitHub push successful. Commit: ${sha.slice(0, 7)}`);
    } catch (err) {
      setStep3Status('error');
      const detail = err.response
        ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
        : err.message;
      saveRaw('github_error', err.response?.data || err.message);
      addLog(`Step 3 error: ${detail}`);
    }
  };

  // ── Step 4: create ADO work item ───────────────────────────────────────────
  const createADOTicket = async () => {
    setStep4Status('loading');
    addLog(`Creating ${workItemType} in ${ADO_PROJECT}…`);

    const featureName = storyTitle.trim();
    const description = buildAdoDescription({ prototypeUrl, demoUrl, prompts: prompts || '(no prompts)' });
    const url = `https://dev.azure.com/${ADO_ORG}/${encodeURIComponent(ADO_PROJECT)}/_apis/wit/workitems/$${encodeURIComponent(workItemType)}?api-version=7.1`;

    const standardFields = [
      { op: 'add', path: '/fields/System.Title',       value: featureName },
      { op: 'add', path: '/fields/System.Description', value: description },
      { op: 'add', path: '/fields/System.State',       value: 'Prototype Review' },
      { op: 'add', path: '/fields/System.Tags',        value: 'mybonsai; prototype; v0dev' },
    ];

    const customFields = [
      { op: 'add', path: '/fields/Custom.PrototypeLink',       value: prototypeUrl },
      { op: 'add', path: '/fields/Custom.GitHubPrototypeRepo', value: githubUrl || '' },
      { op: 'add', path: '/fields/Custom.Prompts',             value: prompts || '' },
    ];

    const postTicket = (fields) => axios.post(url, fields, { headers: adoHeaders });

    try {
      let res;
      let usedCustomFields = true;

      if (customFieldsAvailable !== false) {
        try {
          addLog('Attempting with custom fields…');
          res = await postTicket([...standardFields, ...customFields]);
          setCustomFieldsAvailable(true);
          addLog('Custom fields written successfully.');
        } catch (err) {
          if (isMissingFieldError(err)) {
            addLog('⚠ Custom fields not found — falling back to standard fields.');
            setCustomFieldsAvailable(false);
            usedCustomFields = false;
            res = await postTicket(standardFields);
          } else {
            throw err;
          }
        }
      } else {
        usedCustomFields = false;
        res = await postTicket(standardFields);
      }

      saveRaw('ado_create', res.data);
      const ticketUrl = res.data._links?.html?.href || '';
      const ticketId  = res.data.id;
      setAdoUrl(ticketUrl);
      setAdoTicketId(ticketId);
      addLog(`✓ ADO ticket created: #${ticketId} → ${ticketUrl}`);

      const newTitle = bonsaiTitle(ticketId, featureName);
      try {
        const patchUrl = `https://dev.azure.com/${ADO_ORG}/${encodeURIComponent(ADO_PROJECT)}/_apis/wit/workitems/${ticketId}?api-version=7.1`;
        const patchRes = await axios.patch(
          patchUrl,
          [{ op: 'replace', path: '/fields/System.Title', value: newTitle }],
          { headers: adoHeaders }
        );
        saveRaw('ado_rename', patchRes.data);
        setStoryTitle(newTitle);
        addLog(`✓ Title set to "${newTitle}".`);
      } catch (renameErr) {
        const status = renameErr.response?.status;
        const msg = renameErr.response?.data?.message || renameErr.message;
        saveRaw('ado_rename_error', renameErr.response?.data || renameErr.message);
        addLog(`⚠ Title rename failed (HTTP ${status}): ${msg}`);
      }

      // Component 1: write the v0 source binding to the ticket. We attempt
      // three custom fields first; if any of them are missing in ADO's
      // process (TF401324), we post the same binding as a structured ADO
      // comment instead. The comment marker `[V0-SOURCE-BINDING]` is what
      // the Spec Writer Agent greps for as a fallback when the custom
      // fields aren't populated. Forward-compatible: once you add the
      // fields in ADO, future submissions silently switch back to using
      // them without any code change here.
      if (v0SourceStatus === 'found') {
        const v0SourceFields = [
          { op: 'add', path: '/fields/Custom.V0SourceRepo',   value: v0SourceRepo },
          { op: 'add', path: '/fields/Custom.V0SourceBranch', value: v0SourceBranch },
          { op: 'add', path: '/fields/Custom.V0SourceSha',    value: v0SourceSha },
        ];
        const v0PatchUrl = `https://dev.azure.com/${ADO_ORG}/${encodeURIComponent(ADO_PROJECT)}/_apis/wit/workitems/${ticketId}?api-version=7.1`;
        let v0FieldsWritten = false;
        try {
          await axios.patch(v0PatchUrl, v0SourceFields, { headers: adoHeaders });
          addLog('✓ v0 source binding written to custom fields (V0SourceRepo / Branch / Sha).');
          v0FieldsWritten = true;
        } catch (err) {
          if (isMissingFieldError(err)) {
            addLog('⚠ v0 source custom fields not in ADO process — falling back to structured comment.');
          } else {
            const s = err.response?.status;
            addLog(`⚠ v0 source field write failed (HTTP ${s}): ${err.response?.data?.message || err.message}`);
          }
        }
        if (!v0FieldsWritten) {
          // Structured comment fallback. Markers + HTML <br> so it renders
          // readably in ADO's UI AND remains grep-friendly for the Spec
          // Writer Agent. Lines stay single-key=value to keep parsing dumb.
          // chatName derived the same way fetchPrototype / pushToGitHub do it.
          const chatNameForComment =
            selectedChatObj?.name || selectedChatObj?.title || selectedChatId;
          const lines = [
            '[V0-SOURCE-BINDING]',
            `repo: ${v0SourceRepo}`,
            `branch: ${v0SourceBranch}`,
            `sha: ${v0SourceSha}`,
            `chatId: ${selectedChatId}`,
            `chatName: ${chatNameForComment}`,
            `resolvedAt: ${new Date().toISOString()}`,
          ];
          const commentBody = lines.join('<br>');
          const commentUrl = `https://dev.azure.com/${ADO_ORG}/${encodeURIComponent(ADO_PROJECT)}/_apis/wit/workItems/${ticketId}/comments?api-version=7.1-preview.3`;
          try {
            await axios.post(commentUrl, { text: commentBody }, {
              headers: { Authorization: adoHeaders.Authorization, 'Content-Type': 'application/json' },
            });
            addLog('✓ v0 source binding posted as ADO comment (fallback).');
          } catch (commentErr) {
            const s = commentErr.response?.status;
            addLog(`✗ Comment fallback failed (HTTP ${s}): ${commentErr.response?.data?.message || commentErr.message}. ` +
                   `Binding is in Activity Log only; ticket will need manual edit.`);
          }
        }
      } else {
        addLog(`⚠ No v0 source binding recorded on BONSAI-${ticketId} (status: ${v0SourceStatus}). ` +
               `Spec Writer will fall back to prompts-only context for this ticket.`);
      }

      setStep4Status('success');

      // Persist this submission so the PO has a clear reference to act on next
      // (Spec Draft transition lives in ADO, not in this tool). Survives reload.
      const submission = {
        ticketId,
        ticketTitle: newTitle,
        ticketUrl,
        githubUrl,
        prototypeUrl,
        demoUrl,
        submittedBy: selectedPO || userName,
        submittedAt: new Date().toISOString(),
      };
      setLastSubmission(submission);
      try {
        localStorage.setItem(LAST_SUBMISSION_KEY, JSON.stringify(submission));
      } catch (e) {
        addLog(`Could not persist last submission to localStorage: ${e.message}`);
      }

      // Teams notification
      addLog('Sending Teams notification…');
      try {
        await axios.post('/api/notify-teams', {
          '@type':    'MessageCard',
          '@context': 'http://schema.org/extensions',
          themeColor: '0b8a6e',
          summary:    `New MyBonsai prototype submitted: ${newTitle}`,
          sections: [{
            activityTitle:    '🌿 New Prototype Submitted',
            activitySubtitle: newTitle,
            facts: [
              { name: 'ADO Ticket', value: `[BONSAI-${ticketId}](${ticketUrl})` },
              { name: 'Project',    value: ADO_PROJECT },
              ...(selectedPO ? [{ name: 'Submitted by', value: selectedPO }] : []),
              ...(userEmail   ? [{ name: 'Signed in',   value: userEmail }] : []),
              { name: 'Prototype',  value: `[Open ↗](${prototypeUrl})` },
              ...(demoUrl   ? [{ name: 'Demo',   value: `[Open ↗](${demoUrl})`   }] : []),
              ...(githubUrl ? [{ name: 'GitHub', value: `[Open ↗](${githubUrl})` }] : []),
            ],
            markdown: true,
          }],
        });
        addLog('Teams notification sent.');
      } catch (teamsErr) {
        addLog(`Teams notification failed (local dev — expected): ${teamsErr.response?.status ?? teamsErr.message}`);
      }

    } catch (err) {
      setStep4Status('error');
      const data   = err.response?.data || {};
      const adoMsg = data.message || '';
      const status = err.response?.status;

      let friendlyMsg = `HTTP ${status}: ${adoMsg}`;
      if (status === 401 || status === 403) {
        friendlyMsg = `Auth failed (${status}) — check ADO PAT has Work Items (read/write) scope`;
      } else if (adoMsg.includes('TF401324') || adoMsg.includes('is too long')) {
        friendlyMsg = `Custom field too short — change Custom.Prompts to multiline in ADO process.`;
      } else if (adoMsg.includes('work item type')) {
        friendlyMsg = `Work item type "${workItemType}" not found`;
      } else if (adoMsg.includes('state') || adoMsg.includes('State')) {
        friendlyMsg = `State "Prototype Review" not found`;
      }

      saveRaw('ado_error', data);
      addLog(`✗ Step 4 error: ${friendlyMsg}`);
    }
  };

  // ── Build v0 dropdown ──────────────────────────────────────────────────────
  const buildDropdownItems = () => {
    const items = [];
    items.push(<MenuItem key="__none__" value=""><em>— None —</em></MenuItem>);

    if (projectGroups.length > 0) {
      items.push(
        <ListSubheader key="__proj_hdr__" sx={{ lineHeight: '32px', fontWeight: 700, color: 'primary.main', background: '#f5faf9' }}>
          Projects
        </ListSubheader>
      );
      for (const proj of projectGroups) {
        if (proj.chats.length === 0) {
          items.push(
            <MenuItem key={`__proj_empty_${proj.id}`} disabled sx={{ pl: 3 }}>
              <Typography variant="body2" color="text.disabled" fontStyle="italic">{proj.name} — no chats</Typography>
            </MenuItem>
          );
        } else {
          for (const chat of proj.chats) {
            items.push(
              <MenuItem key={chat.id} value={chat.id} sx={{ pl: 4 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">{proj.name}</Typography>
                  <Typography variant="body2">{chat.name || chat.title || chat.id}</Typography>
                </Box>
              </MenuItem>
            );
          }
        }
      }
    }

    if (standaloneChats.length > 0) {
      items.push(
        <ListSubheader key="__standalone_hdr__" sx={{ lineHeight: '32px', fontWeight: 700, color: 'primary.main', background: '#f5faf9' }}>
          Standalone Chats
        </ListSubheader>
      );
      for (const chat of standaloneChats) {
        items.push(
          <MenuItem key={chat.id} value={chat.id}>
            <Typography variant="body2">{chat.name || chat.title || chat.id}</Typography>
          </MenuItem>
        );
      }
    }

    if (projectGroups.length === 0 && standaloneChats.length === 0) {
      items.push(
        <MenuItem key="__empty__" disabled><em>No projects or chats found via API</em></MenuItem>
      );
    }

    return items;
  };

  // ── Pre-auth render: loading screens for MSAL startup states ──────────────
  // Order matters — check inProgress first because isAuthenticated can flicker
  // false during MSAL's async startup.
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return <AuthLoadingScreen message="Loading…" />;
  }
  if (inProgress === InteractionStatus.Login) {
    return <AuthLoadingScreen message="Signing in to Microsoft…" />;
  }
  if (inProgress === InteractionStatus.Logout) {
    return <AuthLoadingScreen message="Signing out…" />;
  }
  if (!isAuthenticated) {
    // The loginRedirect effect will fire any moment now — show "Redirecting…"
    return <AuthLoadingScreen message="Redirecting to sign-in…" />;
  }

  // ── Authenticated render ───────────────────────────────────────────────────
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AppBar position="static" elevation={1} sx={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <Box component="img" src="/mybonsai-logo.svg" alt="MyBonsai" sx={{ height: 40, mr: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1, color: '#3B3D44' }}>
            Submission Tool
          </Typography>
          {selectedPO && (
            <Chip
              label={`PO: ${selectedPO}`}
              size="small"
              sx={{ mr: 1, background: '#e8f5f0', color: '#0b8a6e', fontWeight: 600, fontSize: 11 }}
            />
          )}
          <Chip
            label={`→ ${ADO_PROJECT}`}
            size="small"
            sx={{ mr: 1, background: '#e8f5f0', color: '#0b8a6e', fontWeight: 600, fontSize: 11 }}
          />
          {/* Signed-in identity + sign-out (always present once authenticated) */}
          <Chip
            label={userName}
            size="small"
            title={userEmail || ''}
            sx={{ mr: 1, background: '#f5f5f5', color: '#3B3D44', fontWeight: 600, fontSize: 11 }}
          />
          <Link
            component="button"
            onClick={handleSignOut}
            sx={{ fontSize: 12, color: '#999', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Sign out
          </Link>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>

        {lastSubmission && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={resetForm}
                sx={{ fontWeight: 700 }}
              >
                Start new submission
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Last submitted:{' '}
              <Link href={lastSubmission.ticketUrl} target="_blank" rel="noreferrer">
                BONSAI-{lastSubmission.ticketId}
              </Link>{' '}
              — {lastSubmission.ticketTitle?.replace(/^BONSAI-\d+:\s*/, '')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(lastSubmission.submittedAt).toLocaleString()}
              {lastSubmission.submittedBy ? ` · by ${lastSubmission.submittedBy}` : ''}
              {lastSubmission.githubUrl ? (
                <> · <Link href={lastSubmission.githubUrl} target="_blank" rel="noreferrer">prototype.md</Link></>
              ) : null}
              {lastSubmission.demoUrl ? (
                <> · <Link href={lastSubmission.demoUrl} target="_blank" rel="noreferrer">demo</Link></>
              ) : null}
            </Typography>
          </Alert>
        )}

        {/* ── PO Selector ──────────────────────────────────────────────── */}
        {AVAILABLE_POS.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={700}>
                  Submitting as:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>PO</InputLabel>
                  <Select value={selectedPO} label="PO" onChange={handlePOChange}>
                    <MenuItem value=""><em>Default (legacy key)</em></MenuItem>
                    {AVAILABLE_POS.map((po) => (
                      <MenuItem key={po} value={po}>{po}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedPO && !selectedPOMissingKey && (
                  <Chip
                    label={`Using ${selectedPO}'s v0.dev key`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedPOMissingKey && (
                  <Chip
                    label={`No key set for ${selectedPO} — using default`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              {selectedPOMissingKey && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>REACT_APP_V0_API_KEY_{getKeySuffix(selectedPO)}</strong> is empty in <code>.env</code>.
                  Falling back to the legacy <code>REACT_APP_V0_API_KEY</code>.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {customFieldsAvailable === false && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Custom fields not set up yet</strong> — tickets are creating with standard fields only.
          </Alert>
        )}
        {customFieldsAvailable === true && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ✓ Custom fields active.
          </Alert>
        )}

        {/* ── Step 1 ─────────────────────────────────────────────────────── */}
        <StepCard title={<>Step 1 — Select v0.dev Chat <StatusChip status={step1Status} /></>}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
            <FormControl sx={{ minWidth: 280, flex: 1 }} size="small">
              <InputLabel>Select project / chat</InputLabel>
              <Select
                value={selectedChatId}
                label="Select project / chat"
                onChange={handleChatSelect}
                renderValue={(val) => {
                  if (!val) return <em>— None —</em>;
                  let found = null;
                  for (const proj of projectGroups) {
                    found = proj.chats.find((c) => c.id === val);
                    if (found) break;
                  }
                  if (!found) found = standaloneChats.find((c) => c.id === val);
                  return found ? (found.name || found.title || val) : val;
                }}
              >
                {buildDropdownItems()}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
              startIcon={step1Status === 'loading' ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={fetchProjects}
              disabled={step1Status === 'loading'}
            >
              Refresh
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Or enter a chat ID manually:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Chat ID"
              size="small" sx={{ flex: 1 }}
              value={selectedChatId}
              onChange={(e) => setSelectedChatId(e.target.value)}
              placeholder="e.g. OMhQPaoaVzc"
              helperText="Paste the chat ID from the v0.dev URL"
            />
            <Button
              variant="outlined"
              sx={{ mt: 0.25, whiteSpace: 'nowrap' }}
              startIcon={step2Status === 'loading' ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => fetchPrototype(selectedChatId, selectedChatObj)}
              disabled={!selectedChatId || step2Status === 'loading'}
            >
              Fetch Details
            </Button>
          </Box>
        </StepCard>

        {/* ── Step 2 ─────────────────────────────────────────────────────── */}
        <StepCard title={<>Step 2 — Prototype Details <StatusChip status={step2Status} /></>}>
          <TextField
            label="Prototype URL" size="small" fullWidth
            placeholder="https://v0.dev/chat/…"
            value={prototypeUrl}
            onChange={(e) => setPrototypeUrl(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: prototypeUrl
                ? <Link href={prototypeUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 12, whiteSpace: 'nowrap', ml: 1 }}>Open ↗</Link>
                : null,
            }}
          />
          <TextField
            label="Demo URL" size="small" fullWidth
            placeholder="https://v0.dev/…/sandbox"
            value={demoUrl}
            onChange={(e) => setDemoUrl(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: demoUrl
                ? <Link href={demoUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 12, whiteSpace: 'nowrap', ml: 1 }}>Open ↗</Link>
                : null,
            }}
          />

          {promptsEmpty && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No prompts found — please add a description in v0.dev
            </Alert>
          )}
          <TextField
            label="Prompts" multiline rows={6} fullWidth size="small"
            value={prompts}
            onChange={(e) => { setPrompts(e.target.value); setPromptsEmpty(!e.target.value.trim()); }}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            helperText={prompts ? `${prompts.length} chars · numbered as [1] [2] [3]…` : 'Auto-extracted from the v0.dev chat.'}
          />

          <TextField
            label="Feature Name" size="small" fullWidth
            value={storyTitle}
            onChange={(e) => setStoryTitle(e.target.value)}
            placeholder="e.g. Goals Summary Page"
            helperText={
              adoTicketId
                ? `Final title: BONSAI-${adoTicketId}: ${storyTitle.replace(/^BONSAI-\d+:\s*/, '')}`
                : `Final title will be: BONSAI-{id}: ${storyTitle || '<feature name>'}`
            }
          />
        </StepCard>

        {/* v0 Design Capture (read-only preview) */}
        {/* Surfaces what fetchPrototype() captured so the PO can visually
            verify the three design sources before Step 3 pushes them to
            GitHub:
              - User attachments (the real design source: logos, mockups,
                HTML templates the PO uploaded into the v0 chat)
              - v0 final screenshot (v0's own rendering of the design)
              - Assistant decisions log (every hex code and font v0 mentioned)
            v0's generated source code is NOT shown here — it lives in
            `Bonsai-LLC/mybonsai_version_1`, resolved separately at submission
            time and recorded in ADO custom fields. Purely derived state,
            no edit handlers. */}
        {step2Status === 'success' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  v0 Design Capture
                </Typography>
                <Chip
                  label={`${v0Attachments.length} attachments`}
                  size="small"
                  color={v0Attachments.length > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
                <Chip
                  label={v0Screenshot ? '1 screenshot' : 'no screenshot'}
                  size="small"
                  color={v0Screenshot ? 'primary' : 'default'}
                  variant="outlined"
                />
                <Chip
                  label={`${v0AssistantChanges.length} decisions`}
                  size="small"
                  color={v0AssistantChanges.length > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Component 1: v0 source binding status. Always visible (not
                  hidden behind the no-capture warning) because the binding is
                  what the Spec Writer Agent will clone — it's the most
                  important field on this card. */}
              <Box
                sx={{
                  mb: 2, p: 1.5, borderRadius: 1,
                  background:
                    v0SourceStatus === 'found'     ? '#f0fdf4' :
                    v0SourceStatus === 'searching' ? '#fafafa' :
                                                     '#fff7ed',
                  border: '1px solid',
                  borderColor:
                    v0SourceStatus === 'found'     ? '#86efac' :
                    v0SourceStatus === 'searching' ? '#e5e7eb' :
                                                     '#fdba74',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                  v0 Source Repo (cloned by the Spec Writer Agent in Pass 1)
                </Typography>
                {v0SourceStatus === 'searching' && (
                  <Box sx={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={12} /> Searching {v0SourceRepo} for a matching branch…
                  </Box>
                )}
                {v0SourceStatus === 'idle' && (
                  <Box sx={{ fontSize: 13, color: '#666' }}>
                    Will resolve after Step 2 fetch completes.
                  </Box>
                )}
                {(v0SourceStatus === 'found' || v0SourceStatus === 'not-found' || v0SourceStatus === 'error') && (
                  <>
                    <Box sx={{ fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6, mb: 1 }}>
                      <strong>{v0SourceRepo}</strong>
                      {v0SourceBranch ? (
                        <span style={{ color: '#666' }}>
                          {' @ '}{v0SourceBranch}
                          {v0SourceSha ? ` (SHA: ${v0SourceSha.slice(0, 12)}…)` : ''}
                        </span>
                      ) : null}
                    </Box>
                    {v0Branches.length > 0 ? (
                      <FormControl size="small" fullWidth required error={!v0SourceBranch}>
                        <InputLabel>v0 branch for this chat</InputLabel>
                        <Select
                          label="v0 branch for this chat"
                          value={v0SourceBranch || ''}
                          onChange={(e) => handleManualBranchSelect(e.target.value)}
                        >
                          {v0Branches.map((b) => (
                            <MenuItem key={b.name} value={b.name}>
                              {b.name}{b.sha ? ` (${b.sha.slice(0, 7)})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', mt: 0.5, color: v0SourceBranch ? 'text.secondary' : 'error.main' }}
                        >
                          {v0SourceBranch
                            ? v0SourceMessage
                            : 'Required — select the branch this chat was pushed to in v0. Auto-match found none.'}
                        </Typography>
                      </FormControl>
                    ) : (
                      <Alert severity="warning" sx={{ py: 0.5, fontSize: 12, '& .MuiAlert-message': { padding: '4px 0' } }}>
                        {v0SourceMessage || 'No branches available to select — refetch or check that the chat is connected to GitHub in v0.'}
                      </Alert>
                    )}
                  </>
                )}
              </Box>

              {v0Attachments.length === 0 && !v0Screenshot && v0AssistantChanges.length === 0 ? (
                <Alert severity="warning">
                  No design assets, screenshot, or decisions captured from this
                  v0 chat. The Spec Writer will fall back to prompts-only context.
                  This is normal for empty chats; for active chats it usually
                  means the v0 API shape has changed.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These three sources will be pushed to{' '}
                    <code>prototypes/{selectedChatId}/</code> on Step 3 and
                    consumed by the Spec Writer in Pass 1. v0's generated
                    source code is pulled separately from{' '}
                    <code>{V0_SOURCE_REPO}</code> (see ADO custom
                    fields on the created ticket).
                  </Typography>

                  {/* ----- User-uploaded attachments ----- */}
                  {v0Attachments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        PO-uploaded design source ({v0Attachments.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Images go to <code>assets/</code>, text files to <code>attachments/</code>.
                      </Typography>
                      {v0Attachments.map((a, i) => {
                        const sanitized = buildAttachmentPath(a, i);
                        const subdir = a.isImage ? 'assets' : 'attachments';
                        const ghPath = `prototypes/${selectedChatId || '<chat-id>'}/${subdir}/${sanitized}`;
                        const tooLarge = a.size > 900_000;
                        const skipReason = tooLarge ? `too large (${a.size}B)` : (!sanitized ? 'invalid path' : null);
                        const willPush = !skipReason;
                        return (
                          <Accordion
                            key={`v0att_${i}`}
                            disableGutters
                            TransitionProps={{ unmountOnExit: true }}
                            sx={{ '&:before': { display: 'none' }, mb: 0.5, border: '1px solid #eee', boxShadow: 'none' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                background: '#fafafa',
                                '&:hover': { background: '#f0f0f0' },
                                '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                                  title={`${a.name} → ${sanitized}`}
                                >
                                  {a.isImage ? '🖼 ' : '📄 '}{sanitized}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={willPush ? `push to ${subdir}/` : `skip: ${skipReason}`}
                                  color={willPush ? 'success' : 'default'}
                                  variant="outlined"
                                  sx={{ fontSize: 10, height: 20, flexShrink: 0 }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  {a.size.toLocaleString()} B
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ background: '#fff' }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                <strong>GitHub path:</strong> <code>{ghPath}</code>
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                <strong>Source:</strong>{' '}
                                <Link href={a.url} target="_blank" rel="noreferrer">v0 blob ↗</Link>{' '}
                                · message #{a.messageIndex} ({a.messageRole})
                                · {a.contentType || 'unknown type'}
                              </Typography>
                              {a.isImage ? (
                                <Box
                                  component="img"
                                  src={a.url}
                                  alt={a.name}
                                  sx={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    maxHeight: 360,
                                    border: '1px solid #ddd',
                                    borderRadius: 1,
                                    background: '#fafafa',
                                    mt: 1,
                                  }}
                                />
                              ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  Text attachment - preview not loaded here.{' '}
                                  <Link href={a.url} target="_blank" rel="noreferrer">Open in new tab</Link>
                                </Alert>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}

                  {/* ----- v0 screenshot ----- */}
                  {v0Screenshot && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        v0 final screenshot
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        v0's own rendering of the final design. Needs Bearer auth
                        to fetch, so cannot be previewed inline here; will be fetched
                        and pushed to <code>assets/v0-final-screenshot.png</code> on Step 3.
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, border: '1px solid #eee', borderRadius: 1, background: '#fafafa' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          version {v0Screenshot.versionId}
                        </Typography>
                        <Chip size="small" label="push to assets/" color="success" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                      </Box>
                    </Box>
                  )}

                  {/* ----- Assistant decisions log (design summary + drill-down) ----- */}
                  {v0AssistantChanges.length > 0 && (() => {
                    const summary = summarizeDecisions(v0AssistantChanges);
                    return (
                      <Box sx={{ mb: 2, border: '1px solid #ddd', borderRadius: 1, p: 2, background: '#fdfdfd' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            v0 design decisions log
                          </Typography>
                          <Chip
                            size="small"
                            label={v0AssistantChanges.length}
                            color="primary"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            Pushed as assistant-log.md on Step 3
                          </Typography>
                        </Box>

                        {/* Design highlights panel - extracted facts, ALWAYS visible */}
                        <Box sx={{ background: '#f0f6f4', border: '1px solid #d8e6e1', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
                            Design highlights extracted from the log
                          </Typography>

                          {/* Colours row */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                            <Typography variant="caption" sx={{ minWidth: 72, fontWeight: 600, color: '#444', flexShrink: 0, mt: 0.25 }}>
                              Colours
                            </Typography>
                            {summary.hex.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                                {summary.hex.map((hex) => (
                                  <Box
                                    key={hex}
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      px: 0.75,
                                      py: 0.25,
                                      background: '#fff',
                                      border: '1px solid #ccc',
                                      borderRadius: '10px',
                                      height: 20,
                                      fontSize: 10,
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: hex, border: '1px solid rgba(0,0,0,0.15)' }} />
                                    {hex}
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontStyle="italic">(none mentioned)</Typography>
                            )}
                          </Box>

                          {/* Fonts row */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                            <Typography variant="caption" sx={{ minWidth: 72, fontWeight: 600, color: '#444', flexShrink: 0, mt: 0.25 }}>
                              Fonts
                            </Typography>
                            {summary.fonts.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                                {summary.fonts.map((font) => (
                                  <Chip
                                    key={font}
                                    size="small"
                                    label={font}
                                    variant="outlined"
                                    sx={{ fontSize: 10, height: 20, fontFamily: 'serif', '& .MuiChip-label': { px: 0.75 } }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontStyle="italic">(none mentioned)</Typography>
                            )}
                          </Box>

                          {/* Files row */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                            <Typography variant="caption" sx={{ minWidth: 72, fontWeight: 600, color: '#444', flexShrink: 0, mt: 0.25 }}>
                              Files
                            </Typography>
                            {summary.files.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                                {summary.files.map((file) => (
                                  <Chip
                                    key={file}
                                    size="small"
                                    label={file}
                                    variant="outlined"
                                    sx={{ fontFamily: 'monospace', fontSize: 10, height: 20, '& .MuiChip-label': { px: 0.75 } }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontStyle="italic">(none mentioned)</Typography>
                            )}
                          </Box>

                          {/* Revisions row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 72, fontWeight: 600, color: '#444', flexShrink: 0 }}>
                              Revisions
                            </Typography>
                            <Typography variant="caption" sx={{ color: summary.revisions > 0 ? '#b45309' : '#999' }}>
                              {summary.revisions > 0
                                ? `${summary.revisions} of ${v0AssistantChanges.length} responses are corrections / apologies`
                                : 'No correction responses detected'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Drill-down: individual responses, collapsed by default */}
                        <Accordion
                          disableGutters
                          defaultExpanded={false}
                          TransitionProps={{ unmountOnExit: true }}
                          sx={{ '&:before': { display: 'none' }, border: '1px solid #eee', boxShadow: 'none' }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              background: '#fafafa',
                              '&:hover': { background: '#f5f5f5' },
                              minHeight: 36,
                              '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden', margin: '8px 0' },
                              '& .MuiAccordionSummary-content.Mui-expanded': { margin: '8px 0' },
                            }}
                          >
                            <Typography variant="caption" fontWeight={600}>
                              Show all {v0AssistantChanges.length} individual responses
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 1.5 }}>
                            {v0AssistantChanges.map((c) => {
                              const preview = meaningfulFirstLine(c.content);
                              const hexCodes = extractHexCodes(c.content, 3);
                              const isRevision = /^(you'?re right|i apologi[sz]e|sorry|let me (fix|try|recreate)|my apolog)/i.test(String(c.content || '').trim());
                              return (
                                <Accordion
                                  key={`v0dec_${c.index}`}
                                  disableGutters
                                  TransitionProps={{ unmountOnExit: true }}
                                  sx={{ '&:before': { display: 'none' }, mb: 0.5, border: '1px solid #eee', boxShadow: 'none' }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                      background: isRevision ? '#fff8e7' : '#fafafa',
                                      '&:hover': { background: isRevision ? '#fff1cc' : '#f0f0f0' },
                                      minHeight: 36,
                                      '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden', margin: '8px 0' },
                                      '& .MuiAccordionSummary-content.Mui-expanded': { margin: '8px 0' },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          minWidth: 36,
                                          fontFamily: 'monospace',
                                          flexShrink: 0,
                                          color: isRevision ? '#b45309' : '#888',
                                          fontWeight: isRevision ? 700 : 400,
                                        }}
                                        title={isRevision ? 'Revision / apology response' : 'Original decision'}
                                      >
                                        {isRevision ? '↻' : '·'} #{c.index}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontSize: 12.5 }}
                                        title={c.content.slice(0, 300)}
                                      >
                                        {preview}
                                      </Typography>
                                      {hexCodes.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                          {hexCodes.map((hex) => (
                                            <Box
                                              key={hex}
                                              title={hex}
                                              sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                background: hex,
                                                border: '1px solid rgba(0,0,0,0.15)',
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      )}
                                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 40, textAlign: 'right', fontSize: 10 }}>
                                        {c.content.length.toLocaleString()}
                                      </Typography>
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ background: '#fff' }}>
                                    {c.createdAt && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        <strong>Sent:</strong> {c.createdAt}
                                      </Typography>
                                    )}
                                    <Box
                                      component="pre"
                                      sx={{
                                        m: 0, p: 1.5,
                                        background: '#0d1117', color: '#c9d1d9',
                                        borderRadius: 1, fontSize: 11,
                                        maxHeight: 400, overflow: 'auto',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                      }}
                                    >
                                      {c.content.length > 5000
                                        ? c.content.slice(0, 5000) + '\n\n... (truncated, ' + (c.content.length - 5000).toLocaleString() + ' more chars hidden)'
                                        : c.content}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              );
                            })}
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    );
                  })()}

                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Step 3 ─────────────────────────────────────────────────────── */}
        <StepCard title={<>Step 3 — Push to GitHub <StatusChip status={step3Status} /></>}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Writes prototype.md, assistant-log.md, the v0 screenshot, and all
            PO-uploaded attachments to{' '}
            <code>{GITHUB_OWNER}/{GITHUB_REPO}/prototypes/{selectedChatId || '<chat-id>'}/</code>.
          </Typography>
          <Button
            variant="contained"
            onClick={pushToGitHub}
            disabled={!prototypeUrl || step3Status === 'loading'}
            startIcon={step3Status === 'loading' ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {step3Status === 'loading' ? 'Pushing…' : (() => {
              const parts = [];
              if (v0Attachments.length) {
                parts.push(`${v0Attachments.length} attachment${v0Attachments.length === 1 ? '' : 's'}`);
              }
              if (v0Screenshot) parts.push('screenshot');
              if (v0AssistantChanges.length) parts.push('log');
              parts.push('prototype.md');
              return `Push to GitHub (${parts.join(' + ')})`;
            })()}
          </Button>
          {githubUrl && (
            <Box sx={{ mt: 2 }}>
              <Link href={githubUrl} target="_blank" rel="noreferrer">View file on GitHub ↗</Link>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                commit: {commitSha.slice(0, 7)}
              </Typography>
            </Box>
          )}
        </StepCard>

        {/* ── Step 4 ─────────────────────────────────────────────────────── */}
        <StepCard title={<>Step 4 — Create Azure Boards Work Item <StatusChip status={step4Status} /></>}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Creates a <strong>{workItemType}</strong> in <strong>{ADO_PROJECT}</strong>.
          </Alert>

          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Work Item Type</InputLabel>
            <Select value={workItemType} label="Work Item Type" onChange={(e) => setWorkItemType(e.target.value)}>
              <MenuItem value="User Story">User Story</MenuItem>
              <MenuItem value="Bug">Bug</MenuItem>
              <MenuItem value="Task">Task</MenuItem>
              <MenuItem value="Feature">Feature</MenuItem>
              <MenuItem value="Epic">Epic</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Feature Name" size="small" fullWidth
            value={storyTitle}
            onChange={(e) => setStoryTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            startIcon={step4Status === 'loading' ? <CircularProgress size={16} color="inherit" /> : <BugReportIcon />}
            onClick={createADOTicket}
            disabled={!storyTitle || !v0SourceBranch || step4Status === 'loading'}
          >
            Create {workItemType}
          </Button>
          {step2Status === 'success' && !v0SourceBranch && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              Select the v0 source branch in the Design Capture section above before creating the ticket.
            </Typography>
          )}
          {adoUrl && (
            <Box sx={{ mt: 2 }}>
              <Link href={adoUrl} target="_blank" rel="noreferrer">
                Open BONSAI-{adoTicketId} in Azure Boards ↗
              </Link>
            </Box>
          )}
        </StepCard>

        {/* ── Activity Log ──────────────────────────────────────────────── */}
        <Card sx={{ mb: 3, background: '#1e1e1e' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#9cdcfe' }}>Activity Log</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small"
                  onClick={() => handleCopy('log', log.join('\n'))}
                  sx={{ color: copiedKey === 'log' ? '#0b8a6e' : '#9cdcfe', fontSize: 11, fontWeight: copiedKey === 'log' ? 700 : 400 }}>
                  {copiedKey === 'log' ? 'Copied!' : 'Copy'}
                </Button>
                <Button size="small" onClick={() => setLog([])} sx={{ color: '#666', fontSize: 11 }}>Clear</Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#333' }} />
            {log.length === 0
              ? <Typography variant="body2" sx={{ color: '#666' }}>No activity yet.</Typography>
              : log.map((entry, i) => (
                <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: '#d4d4d4', mb: 0.5, wordBreak: 'break-all' }}>
                  {entry}
                </Typography>
              ))
            }
          </CardContent>
        </Card>

        {Object.keys(rawResponses).length > 0 && (
          <Card sx={{ background: '#0d1117' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#58a6ff' }}>Raw API Responses</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small"
                    onClick={() => handleCopy('raw_all', JSON.stringify(rawResponses, null, 2))}
                    sx={{ color: copiedKey === 'raw_all' ? '#0b8a6e' : '#58a6ff', fontSize: 11, fontWeight: copiedKey === 'raw_all' ? 700 : 400 }}>
                    {copiedKey === 'raw_all' ? 'Copied!' : 'Copy All'}
                  </Button>
                  <Button size="small" onClick={() => setRawResponses({})} sx={{ color: '#666', fontSize: 11 }}>Clear</Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 2, borderColor: '#222' }} />
              {Object.entries(rawResponses).map(([key, val]) => (
                <Box key={key} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#f78166', fontWeight: 700 }}>{key}</Typography>
                    <Button size="small"
                      onClick={() => handleCopy(`raw_${key}`, JSON.stringify(val, null, 2))}
                      sx={{ color: copiedKey === `raw_${key}` ? '#0b8a6e' : '#58a6ff', fontSize: 10, py: 0, fontWeight: copiedKey === `raw_${key}` ? 700 : 400 }}>
                      {copiedKey === `raw_${key}` ? 'Copied!' : 'Copy'}
                    </Button>
                  </Box>
                  <Box component="pre" sx={{
                    m: 0, p: 1.5, background: '#161b22', borderRadius: 1,
                    fontSize: 12, color: '#c9d1d9', overflowX: 'auto',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  }}>
                    {JSON.stringify(val, null, 2)}
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

      </Container>
    </ThemeProvider>
  );
}
